import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Container,
  Grid,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Modal,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Tooltip,
  IconButton,
} from "@mui/material";
import { attentionColors, mobiliTheme } from "../../../../../themes/mobiliTheme";
import {
  Add,
  Delete,
} from "@mui/icons-material";
import StartIcon from "@mui/icons-material/Start";
import Controls from "../../../../../atoms/Controls";
import TextArea from "../../../../../atoms/TextArea";
import { getKvnrError } from "../../../../../utils/kvnrValidator";
import { observer } from "mobx-react";
import { initialValues } from "../../../../pages/Patient/initialValues";
import useStore from "../../../../../helpers/useStore";
import {
  useMasterTables,
  getTherapyId,
  resolveTarifGroupForRxType,
  useIcdCodes
} from "../../../../../stores/MasterTables";
import { DoctorContext } from "../../../../../stores/Doctor/doctor.provider";
import { RxStoreContext } from "../../../../../stores/Rx/rx.provider";
import { NoteDto, RxDto, RxDtoProcessingStatusEnum } from "../../../../../api-client";
import { v4 as uuidv4 } from "uuid";
import notificationStore from "../../../../../stores/Notification/notificationStore";
import { confirmDoctorClinicJoin } from "../../../../DoctorManagement/confirmDoctorClinicJoin";
import { confirmDoctorClinicMismatch } from "../../../../DoctorManagement/confirmDoctorClinicMismatch";
import { UserContext } from "../../../../../stores/User/User.provider";
import { useUserAccounts } from "../../../../../stores/User/User.hooks";
import DoctorDialog from "../../../../PatientDataEntryForm/Doctor";
import { PatientsContext } from "../../../../../stores/Patients/patients.provider";
import FlatCard from "../../../../../atoms/FlatCard";
import classes from "./Verordnung13.module.css";
import ValidationCard, { formType } from "../../../../../molecules/ValidationCard";
import WarningDialog from "../../../../../atoms/WarningDialog";
import debounce from "lodash.debounce";
import ValidationTooltip from "../../../../../atoms/validationToolTip";
import DoctorCard from "../../../../../molecules/DoctorCard";
import { NotesContext } from "../../../../../stores/Notes/notes.provider";
import Notes from "../../../../ScheduleOverview/Notes/Notes";
import { validateFrequency, validateTherapy } from "../../../../../molecules/Editable";
import { rxuseForm } from "../../../../PatientDataEntryForm/Form";
import {
  ClinicForModal,
  Doctor,
} from "../../../../DoctorManagement/DoctorTabContent/doctor-client-types";
import VerordnungDialog from "./VerordnungDialog";
import SimpleDocumentViewer from "../../../../Documents/SimpleDocumentViewer";
import { useRx } from "../../../../../stores/Rx/rx.queries";
import { usePatient } from "../../../../../stores/Patients/patient.queries";
import { useSaveRxs } from "../../../../../stores/Rx/rx.mutations";
import { EntryType } from "../../../../PatientDataEntryForm/Receipt";
import moment from "moment";
import { formTherapyFromLine } from "../../../../../helpers/therapyRxLabel";
import { RxFormRow, rowNamesTerm, rowTermText } from "../../../../../helpers/rxFormRow";
import TherapyTermField from "../../../../../molecules/TherapyTermField";
import { usePrescribedTherapyOptions } from "../../../../../stores/MasterTables/masterTables.queries";

interface DiagnoseGroup {
  a: string;
  b: string;
  c: string;
  d: string;
}

interface Verordnung13Props {
  selectedRxId?: string;
  patientId?: number;
  handleNewFormData?: (data: any) => void;
  newFormData?: any;
  values?: any;
  readOnlyView?: boolean;
  entryType?: EntryType;
  openRx?: boolean;
  onCloseRx?: any;
  onRxSave?: (rxId: string) => void;
  updateRxProcessingStatus?: (event: any) => void;
}

const ScalableDiv = styled("div")(({ theme }) => ({
  transformOrigin: "top left",
  [theme.breakpoints.down("sm")]: {
    transform: "scale(0.6)",
  },
  [theme.breakpoints.between("sm", "xl")]: {
    transform: "scale(0.65)",
  },
  [theme.breakpoints.up("xl")]: {
    transform: "scale(0.85)",
  },
}));

const ScalableComponentSimple = ({ children }: any) => {
  return <ScalableDiv>{children}</ScalableDiv>;
};

const Verordnung13: React.FC<Verordnung13Props> = observer(
  ({
    selectedRxId,
    patientId,
    handleNewFormData,
    newFormData,
    values,
    readOnlyView,
    entryType,
    onCloseRx,
    onRxSave,
    openRx,
    updateRxProcessingStatus,
  }) => {
    // const { rxFormData, patientValues, handleData, masterTablesData, readOnlyView } = props;
    const { data: masterTablesDataQuery } = useMasterTables();
    // Prescribed TERMS — the catalog level a prescription names (MD-232).
    const { data: termOptions = [] } = usePrescribedTherapyOptions();
    // Fetch RX data using the rxId from store
    const { data: rxData, isLoading: isRxLoading, error: rxError } = useRx(selectedRxId!);
    const rxFormData = entryType && newFormData ? newFormData : rxData;
    // console.log("RxFormData in Verordnung13", rxFormData);

    // Get patient data from store
    const { data: patientData } = usePatient(patientId!);
    const patientValues = entryType === EntryType.newRx && values ? values : patientData;
    const { mutate: patientSaveRxs, isPending: isSavingRx } = useSaveRxs();
    const [currentICD, setCurrentICD] = useState(null)
    const { data: icdObject } = useIcdCodes(currentICD,false); // disabled initially
    // Use prop masterTablesData or fallback to query data
    const safeMasterTablesData = masterTablesDataQuery || [];
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [unsavedChangesWarning, setShowunsavedChangesWarning] = useState(false);
    const UserStore = useStore(UserContext);
    const accounts = useUserAccounts();
    const patientStore = useStore(PatientsContext);
    const rxStore = useStore(RxStoreContext);
    const { savedSeriesForPatient } = rxStore;
    const { getLiveValidation } = patientStore;
    const { user } = UserStore;
    // TODO: Replace getICD with appropriate hook-based solution
    const [selectedDiagnoseGroup, setSelectedDiagnoseGroup] = useState<DiagnoseGroup | null>(null);
    const RxStore = useStore(RxStoreContext);
    const { saveRxs } = RxStore;
    const [loading, setLoading] = useState(false);

    //For payer
    const insurer = safeMasterTablesData
      .filter((obj: any) => "insurer" in obj)
      .map((obj: any) => obj.insurer);
    const [selectedPayer, setSelectedPayer] = useState(null);
    const INSURANCE_FIELD_LABELS: Record<string, string> = {
      chipcard: "Kostenträgerkennung",
      insureeNumber: "Versicherten-Nr.",
      insuranceStatus: "Status",
    };
    // Fields the user copied from the patient (used to show the blue bar).
    const [appliedFields, setAppliedFields] = useState<Record<string, boolean>>({});
    const diagnoseGroups = safeMasterTablesData
      .filter((obj: any) => "diagnosisGroups" in obj)
      .map((obj: any) => obj.diagnosisGroups);
    const rxType = safeMasterTablesData
      .filter((obj: any) => "rxType" in obj) // Filter objects that have the insuranceStatus property
      .map((obj: any) => obj.rxType);
    //  therapyOptions= therapyOptions[0].filter(
    //       (therapyObj: any) =>

    //         therapyObj.duration
    //     )

    //For Doctor
    const [selectedDoctor, setSelectedDoctor] = useState(null);
    const DoctorStore = useStore(DoctorContext);
    const { getClinicById, getDoctorById, createDoctorWithClinic } = DoctorStore;

    const [isSelectActive, setIsSelectActive] = useState(false);
    const [isAddEditDialogOpen, setAddEditDialogOpen] = useState(false);
    const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
    const NotesStore = useStore(NotesContext);
    const { getRxNotes, updateNoteById, postNotes, fetchNotes } = NotesStore;
    const [rxNotes, setRxNotes] = useState<NoteDto[]>([]);
    useEffect(() => {
      const fetchData = async () => {
        try {
          const response = await getRxNotes(selectedRxId!);

          setRxNotes(response);
        } catch (error) {
          // console.log("error", error);
        }
      };

      fetchData();
    }, [selectedRxId, fetchNotes]);
    const updateNote = async (index: number, updatedCard: NoteDto) => {
      updatedCard.rxId = selectedRxId!;
      if (updatedCard.id) {
        await updateNoteById(updatedCard.id, user?.id as string, updatedCard);
      } else {
        await postNotes(user?.id as string, updatedCard);
      }
      const updatedNotes = [...rxNotes];
      updatedNotes[index] = updatedCard;
      setRxNotes(updatedNotes);
    };

    const account = safeMasterTablesData
      .filter((obj: any) => "account" in obj)
      .map((obj: any) => obj.account);

    const handlePersonSearchClick = () => {
      setIsSelectActive(!isSelectActive); // Activate the Select dropdown
    };

    /**
     * "" is what an untouched form field holds; null is what a nullable column
     * accepts. Only the empty string is converted — false, 0 and a real value
     * all pass through, so an explicitly unticked box stays unticked.
     */
    const emptyToNull = <T,>(value: T): T | null =>
      (value as unknown) === "" ? null : value;

    const initialrxValues = {
      Receiptpayer: patientValues?.payer,
      address: "",

      status: "",
      signatureStatus: "",
      insuranceStatus: "",
      chipcard: "",
      tarifGroupId: "",
      insureeNumber: "",
      rxtype: "",
      accountId: "",

      isPerpetual: false,

      practiceNumber: "",
      doctorNumber: "",
      doctorId: "",
      clinicId: "",
      date: "",
      isCopaymentExempted: false,
      isAccident: false,
      isBVG: false,
      icdCodeEntries: [{ userText: "" }, { userText: "" }],
      diagnosisText: "",
      diagnosegroup: null,
      a: "",
      b: "",
      c: "",
      d: "",
      hasIndividualSymptoms: false,
      therapyRxs: [
        {
          id: uuidv4(),
          amount: "",
          frequency: { id: "", text: "", prefferedValue: "" },
          therapy: {
            id: "",
            HMP4: "",
            price: "",
            abbreviation: "",
            description: "",
            isSupplementary: false,
          },
          // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
          rxSeries: { label: "" },
        },
        {
          id: uuidv4(),
          amount: "",
          frequency: { id: "", text: "", prefferedValue: "" },
          therapy: {
            id: "",
            HMP4: "",
            price: "",
            abbreviation: "",
            description: "",
            isSupplementary: false,
          },
          // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
          rxSeries: { label: "" },
        },
        {
          id: uuidv4(),
          amount: "",
          frequency: { id: "", text: "", prefferedValue: "" },
          therapy: {
            id: "",
            HMP4: "",
            price: "",
            abbreviation: "",
            description: "",
            isSupplementary: false,
          },
          // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
          rxSeries: { label: "" },
        },
        {
          id: uuidv4(),
          amount: "",
          frequency: { id: "", text: "", prefferedValue: "" },
          therapy: {
            id: "",
            HMP4: "",
            price: "",
            abbreviation: "",
            description: "",
            isSupplementary: true,
          },
          // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
          rxSeries: { label: "" },
        },
      ],
      isHomeVisit: rxFormData?.isHomeVisit ?? null,
      therapyReport: false,
      urgent: false,
      symptoms: "",
      therapyFrequency: "",
      amount: "",
      additionalNotes: "",
      clinic: {} as any,
      duplicate: false,
      processingStatus: RxDtoProcessingStatusEnum.InBehandlung,
    };

    const [formData, setFormData] = useState(initialrxValues);
    const [icdCodeEntries, setIcdCodeEntries] = useState(initialrxValues.icdCodeEntries);
    const [mainTherapies, setMainTherapies] = useState([]);
    const [supplementaryTherapies, setSupplementaryTherapies] = useState([]);


    // Patient-only fields: these never live on the Rx entity, so read them
    // straight from the patient record (the single source of truth).
    const patientLastName = patientValues?.lastName || "";
    const patientFirstName = patientValues?.firstName || "";
    const patientDob = patientValues?.dob ? moment(patientValues.dob).format("YYYY-MM-DD") : "";

    const therapyOptions = safeMasterTablesData
      .filter((obj: any) => "therapy" in obj)
      .map((obj: any) => obj.therapy)[0];

    // Derive therapyType from the selected account (Physio vs. Ergo)
    const accountTherapyType = useMemo(() => {
      if (!formData.accountId || !accounts) return "Physiotherapie";
      const selectedAccount = accounts.find(
        (a: any) => a.name === formData.accountId || a.id === formData.accountId
      );
      return selectedAccount?.therapyType || "Physiotherapie";
    }, [formData.accountId, accounts]);

    // Only the groups the chosen Rezept-typ can be billed against — narrowed
    // further to the ones this practice's therapy type actually has line items
    // for, so the invariant cannot be broken by hand either.
    const tarifGroupOptions = useMemo(
      () =>
        resolveTarifGroupForRxType(
          safeMasterTablesData,
          formData?.rxtype,
          formData?.tarifGroupId,
          accountTherapyType
        ).options,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [safeMasterTablesData, formData?.rxtype, formData?.tarifGroupId, accountTherapyType]
    );

    const filterTherapies = () => {
      if (!therapyOptions) return;

      const main = therapyOptions.filter(
        (option: any) => option.tarifGroup.id === formData.tarifGroupId && !option.isSupplementary &&
          (!option.therapyType || option.therapyType === accountTherapyType)
      );

      const supplementary = therapyOptions.filter(
        (option: any) => option.tarifGroup.id === formData.tarifGroupId && option.isSupplementary &&
          (!option.therapyType || option.therapyType === accountTherapyType)
      );

      setMainTherapies(main);
      setSupplementaryTherapies(supplementary);
    };

    useEffect(() => {
      filterTherapies();
    }, [formData.rxtype, formData.tarifGroupId, accountTherapyType]);
    const handleUnsavedChangesWarning = () => {
      handleGenericSubmit();
    };
    // const updateRxProcessingStatus=async(event:any)=>{
    //   const response=await updateprocessingStatus(rxFormData.id,event?.target.value)
    // }
    const handleCloseRxDialog = () => {
      if (unsavedChanges) {
        // If there are unsaved changes, set a state to open the warning dialog
        setShowunsavedChangesWarning(true);
      } else {
        onCloseRx();
      }
    };
    const handleCloseUnsavedChangesWarning = () => {
      onCloseRx();
      // If user cancels and chooses not to continue, close the warning dialog
      setShowunsavedChangesWarning(false);
    };

    const handleOpenDocumentViewer = () => {
      setIsDocumentViewerOpen(true);
    };

    const handleCloseDocumentViewer = () => {
      setIsDocumentViewerOpen(false);
    };

    // useEffect(() => {
    //   if (rxFormData) {
    //     // Populate the form data with values from rxFormData
    //     setFormData(rxFormData);
    //   } else {
    //     // Use empty values for form data
    //    setFormData(initialrxValues)
    //   }
    // }, [rxFormData]);
    // Initialize with two input boxes

    // Expose the submitForm function to the parent component

    const formattedRxData = (rxFormData: any, initialTherapyRxs: any) => {
      let icdCodeEntries;

      if (rxFormData?.icdCodeEntries && rxFormData?.icdCodeEntries.length > 0) {
        if (rxFormData.icdCodeEntries.length === 1) {
          icdCodeEntries = [...rxFormData.icdCodeEntries, { userText: "" }];
        } else {
          icdCodeEntries = rxFormData.icdCodeEntries;
        }
      } else {
        icdCodeEntries = [{ userText: "" }, { userText: "" }];
      }
      const updatedFormData = {
        ...rxFormData,
        diagnosisText: rxFormData?.diagnosisText || "",
        additionalNotes: rxFormData?.additionalNotes || "",
        symptoms: rxFormData?.symptoms || "",

        // Keep these Rx-native. Patient-only fields (name, DOB) are read directly
        // from patientValues at render time and are intentionally NOT copied here.
        insureeNumber: rxFormData?.insureeNumber || "",
        insuranceStatus: rxFormData?.insuranceStatus || "",
        // Receiptpayer: rxFormData.payer.insurer || "",
        Receiptpayer:
          insurer[0]?.find((option: any) => option.id === rxFormData.Receiptpayer)?.name ||
          rxFormData.payer?.name ||
          rxFormData.Receiptpayer,
        chipcard: rxFormData.chipcard || rxFormData.payer?.ik,
        diagnosegroup:
          diagnoseGroups[0]?.find((option: any) => option.id === rxFormData.diagnosegroup)?.code ||
          rxFormData.diagnosisGroup?.code,
        rxtype:
          rxType[0]?.find((option: any) => option.id === rxFormData.rxtype)?.text ||
          rxFormData.rxtype?.text ||
          "",
        // doctorData: rxFormData.doctor,
        accountId:
          account[0]?.find((option: any) => option.id === rxFormData.accountId)?.name ||
          rxFormData.account?.name,
        status: rxFormData.status || "",
        signatureStatus: rxFormData.signatureStatus || "",
        therapyRxs: initialTherapyRxs,
        // rxtype:rxFormData.rxtype?.id || "",
        // accountId:rxFormData.account?.id || "",
        // chipcard: rxFormData.payer?.id || "",
        // doctorId: rxFormData.doctor?.contactInfo?.lastName || "",
        // bsnr: rxFormData.practiceNumber || "",
        // diagnosegroup: rxFormData.diagnosisGroup?.code || "",
        icdCodeEntries: icdCodeEntries,
        // Map new API field names to form's internal keys
        doctorNumber: rxFormData.LANR || "",
        practiceNumber: rxFormData.BSNR || "",
        doctorId: rxFormData.doctorId || rxFormData.doctorNumber || "",
        clinicId: rxFormData.clinicId || rxFormData.practiceNumber || "",
      };
      return updatedFormData;
    };

    useEffect(() => {
      const fetchData = async () => {
        if (rxFormData && rxFormData.rxNumber) {
          // rxFormData = patientValues?.rxs.find((rx: any) => rx.id === rxFormData.id);
          // console.log(
          //   "After checking RxFormdata and Patient Value",
          //   rxFormData,
          //   " and",
          //   patientValues.rxs
          // );
          // let rxs: [] = [];

          rxFormData.therapyRxs = rxFormData?.therapyRxs?.flatMap((therapy: any): RxFormRow => {
            // Extract therapyRxs from each rx, or default to an empty array
            // Map therapyRxs to the desired format
            return {
              id: therapy.id,
              date: rxFormData.date,
              // Carried through the round trip: this is what names the term on
              // save. An API line has `rawText`; fall back to the term id so a
              // line stored before rawText existed still resolves.
              rawText: therapy.rawText ?? therapy.prescribedTherapyId ?? null,
              therapy: formTherapyFromLine(therapy, therapyOptions),
              amount: therapy.amount,
              frequency: therapy.frequency
                ? therapy.frequency.id
                  ? therapy.frequency
                  : { id: therapy?.frequency, text: therapy?.frequency }
                : null,
              // Doubling is `unitsPerDay` since MD-232 — the server no longer
              // sends a persisted `isDoubled`. Carry both: the checkbox reads
              // the boolean, every save channel reads `unitsPerDay`.
              unitsPerDay: therapy.unitsPerDay,
              isDoubled: (therapy.unitsPerDay ?? (therapy.isDoubled ? 2 : 1)) > 1,
              rxSeries: rxFormData.rxSeries ?? therapy.rx?.rxSeries,
            };
          });
          // Check if supplementary therapy exists
          const hasSupplementary = rxFormData?.therapyRxs?.some(
            (therapy: any) => therapy.therapy?.isSupplementary
          );

          let finalTherapyRxs: any[] = [...(rxFormData?.therapyRxs ?? [])];

          // If no supplementary therapy exists, ensure we add one
          if (!hasSupplementary) {
            finalTherapyRxs = [
              ...(rxFormData.therapyRxs ?? []),
              {
                id: uuidv4(),
                amount: "",
                frequency: { id: "", text: "", preferredValue: "" },
                therapy: {
                  id: "",
                  HMP4: "",
                  price: "",
                  abbreviation: "",
                  description: "",
                  isSupplementary: true, // Add supplementary therapy
                },
                // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
                rxSeries: { label: "" },
              },
            ];
          }

          // Ensure we always have 4 therapy rows, keeping existing data intact
          while (finalTherapyRxs.length < 4) {
            finalTherapyRxs.push({
              id: uuidv4(),
              amount: "",
              frequency: { id: "", text: "", prefferedValue: "" },
              therapy: {
                id: "",
                HMP4: "",
                price: "",
                abbreviation: "",
                description: "",
                isSupplementary: false, // Default to main therapy
              },
              // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
              rxSeries: { label: "" },
            });
          }

          // Update form data based on fetched data

          const updatedFormData = formattedRxData(rxFormData, finalTherapyRxs);
          // Update form data state
          setFormData(updatedFormData);

          // Optionally, return the updated form data
          return updatedFormData;
        }
        // Make API request to fetch patient data based on patient ID or any other identifier
        else {
          const hasSupplementary = rxFormData?.therapyRxs?.some(
            (therapy: any) => therapy.therapy?.isSupplementary
          );

          const therapyRxs =
            rxFormData?.therapyRxs && rxFormData?.therapyRxs?.length
              ? Array.from({ length: 4 }, (_, index) => ({
                  ...rxFormData?.therapyRxs?.[index],
                  id: rxFormData?.therapyRxs?.[index]?.id || uuidv4(),
                  amount: rxFormData?.therapyRxs?.[index]?.amount || "",
                  frequency: rxFormData?.therapyRxs?.[index]?.frequency
                    ? rxFormData?.therapyRxs?.[index]?.frequency
                    : { id: "", text: "" },
                  therapy: rxFormData?.therapyRxs?.[index]?.therapy
                    ? rxFormData?.therapyRxs[index].therapy
                    : {
                        id: "",
                        abbreviation: "",
                        isSupplementary: hasSupplementary ? false : index === 3, // Ensure at least one supplementary
                      },
                }))
              : initialrxValues.therapyRxs;

          const updatedData = formattedRxData(rxFormData, therapyRxs);

          // Set form values using therapytableData or default to initialrxValues
          setFormData(updatedData);
        }
      };
      fetchData();

      // Only Rx-native data is snapshotted into formData now, so this effect just
      // tracks rxFormData. Patient-record fields (name, DOB, and the insurance
      // fallbacks) are read directly from patientValues at render time, which also
      // removes the prior timing bug where the patient loading after the Rx left
      // the name blank when navigating directly to an Rx.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rxFormData]);
    useEffect(() => {
      if (formData.diagnosegroup) {
        const diagnoseGroupIsId = diagnoseGroups[0].some(
          (group: any) => group.id === formData.diagnosegroup
        );
        // Find the matching diagnose group based on the selected code
        const selectedDiagnoseGroup = diagnoseGroupIsId
          ? diagnoseGroups[0].find((group: any) => group.id === formData.diagnosegroup)
          : diagnoseGroups[0].find((group: any) => group.code === formData.diagnosegroup);

        if (selectedDiagnoseGroup) {
          // Generate symptoms based on selected checkboxes
          let selectedSymptoms = "";
          if (formData.a) selectedSymptoms += selectedDiagnoseGroup.a + " ";
          if (formData.b) selectedSymptoms += selectedDiagnoseGroup.b + " ";
          if (formData.c) selectedSymptoms += selectedDiagnoseGroup.c + " ";
          if (formData.d) selectedSymptoms += selectedDiagnoseGroup.d + " ";
          setSelectedDiagnoseGroup(selectedDiagnoseGroup);
          setFormData((prevValues: any) => ({
            ...prevValues,
            a: selectedDiagnoseGroup.a ? prevValues.a : false,
            b: selectedDiagnoseGroup.b ? prevValues.b : false,
            c: selectedDiagnoseGroup.c ? prevValues.c : false,
            d: selectedDiagnoseGroup.d ? prevValues.d : false,
            ...(prevValues.hasIndividualSymptoms ? {} : { symptoms: selectedSymptoms.trim() }),
          }));
        }
      }
    }, [formData.diagnosegroup, formData.a, formData.b, formData.c, formData.d]);

    // const handleDeleteTherapy = (therapyId: string) => {
    //   // Filter out the therapy that matches the given ID
    //   const updatedTherapies = formData.therapyRxs.filter((therapy) => therapy.id !== therapyId);

    //   // Update formData state with new therapy list
    //   setFormData({
    //     ...formData,
    //     therapyRxs: updatedTherapies,
    //   });
    // };
    const handleDeleteTherapy = (therapyId: string) => {
      // Map over therapyRxs and clear the matching row's content
      const updatedTherapies = formData.therapyRxs.map((therapy) =>
        therapy.id === therapyId
          ? {
              ...therapy,
              amount: "",
              // frequency: { id: "", text: "", prefferedValue: "" },
              therapy: {
                id: "",
                HMP4: "",
                price: "",
                abbreviation: "",
                description: "",
                isSupplementary: therapy.therapy.isSupplementary,
              },
              // MD-232: the prescribed term as typed. The server resolves it
          // text -> alias -> term; declared here so the line type carries it.
          rawText: null as string | null,
          isDoubled: false,
              rxSeries: { label: "" },
            }
          : therapy
      );

      setFormData({
        ...formData,
        therapyRxs: updatedTherapies,
      });
    };
//Could not find better way to implement this icdCode
    const handleICDCodeEntrySubmit = useCallback(
      async (icdEntry: any, index: number) => {

        try {
          setFormData((prevValues: any) => {
            const updatedICDCodeEntries = prevValues.icdCodeEntries.map((entry: any, i: number) =>
              i === index ? { id: icdEntry.id || "", userText: icdEntry.userText } : entry
            );

            // Avoid duplicating diagnosis text
            const existingICDTexts = prevValues.icdCodeEntries.map((e: any) => e.userText);
            const newDiagnosisText = existingICDTexts.includes(icdObject.text)
              ? prevValues.diagnosisText
              : prevValues.diagnosisText
              ? `${prevValues.diagnosisText} ${icdObject.text}`
              : icdObject.text;

            return {
              ...prevValues,
              icdCodeEntries: updatedICDCodeEntries,
              diagnosisText: newDiagnosisText.trim(),
            };
          });
        } catch (error) {
          console.error("Error updating ICD entry:", error);
        }
      },
      [icdObject]
    );

    useEffect(() => {
      if (icdObject) {
        // Use latest index and currentICD
        const index = icdCodeEntries.findIndex((e) => e.userText === currentICD);
        handleICDCodeEntrySubmit(icdCodeEntries[index], index);
      }
    }, [icdObject]);
    // const handleICDCodeEntrySubmit = async (icdCode: any, index: number) => {
    //   try {
    //     // TODO: Replace getICD call with React Query implementation
    //     // const { data: icdObject } = await icdFetch( icdCode?.userText);
    //     const icdObject: any = null; 
    //     if (icdObject){
    //       // If the API call is successful and returns an icdObject, update the icdCodeEntries state with the normCode
    //       setFormData((prevValues: any) => {
    //         const updatedICDCodeEntries = prevValues.icdCodeEntries.map(
    //           (icdEntry: any, i: number) => {
    //             return i === index
    //               ? { id: icdCode?.id || "", userText: icdCode.userText }
    //               : icdEntry;
    //           }
    //         );

    //         const concatenatedDiagnosisText = prevValues.diagnosisText
    //           ? `${prevValues.diagnosisText} ${(icdObject as any)?.text}` // Append the new icdObject.text to the existing diagnosisText
    //           : (icdObject as any)?.text;
    //         return {
    //           ...prevValues,
    //           icdCodeEntries: updatedICDCodeEntries,
    //           diagnosisText: concatenatedDiagnosisText.trim(), // Set diagnosisText to the concatenated value
    //         };
    //       });
    //     }
    //   } catch (error) {
    //     console.error("Error fetching ICD object:", error);
    //   }
    // };

    const handleAddInput = () => {
      setIcdCodeEntries([...icdCodeEntries, { userText: "" }]);

      // Update the formValues state as well to reflect the new entry
      setFormData((prevValues: any) => ({
        ...prevValues,
        icdCodeEntries: [...prevValues.icdCodeEntries, { userText: "" }],
      }));
    };
    const [isHausbesuchSelected, setHausbesuchSelected] = useState<boolean | null>(
      formData.isHomeVisit
    );
    const handleCheckboxChange = (event: any) => {
      const selectedValue = event.target.value; // "yes" or "no"

      if (selectedValue === "yes") {
        setHausbesuchSelected(true);
        // Set isHomeVisit to true if "yes" is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: true,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      } else if (selectedValue === "no") {
        setHausbesuchSelected(false);
        // Set isHomeVisit to false if "no" is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: false,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      } else {
        setHausbesuchSelected(null); // Set null if neither is selected
        // Set isHomeVisit to null if neither checkbox is selected
        handleInputChange({
          target: {
            name: "isHomeVisit",
            value: null,
          } as unknown as EventTarget & HTMLInputElement, // Cast to EventTarget & HTMLInputElement
        } as React.ChangeEvent<HTMLInputElement>);
      }
    };
    const rxvalidate = (fieldValues = formData) => {
      let temp: any = { ...rxerrors };

      // Validate chipcard (if chipcard exists in fieldValues)
      if ("chipcard" in fieldValues) {
        const chipCardValue = fieldValues.chipcard;
        if (chipCardValue && !insurer[0]?.some((option: any) => option.ik === chipCardValue)) {
          temp.chipcard = "Ungültige Chipkartennummer";
        } else {
          temp.chipcard = ""; // Clear error if valid
        }
      }
      if ("accountId" in fieldValues) temp.accountId = fieldValues.accountId ? "" : "Pflichtfeld";
      if ("tarifGroupId" in fieldValues)
        temp.tarifGroupId = fieldValues.tarifGroupId ? "" : "Pflichtfeld";

      rxsetErrors({
        ...temp,
      });

      if (fieldValues == formData) return Object.values(temp).every((x) => x == "");
    };
    const { rxerrors, rxsetErrors } = rxuseForm(formData, true, rxvalidate);

    /**
     * Why Speichern is unavailable, or "" when it is available.
     *
     * The button has always been disabled by any non-empty entry in `rxerrors`,
     * but only `accountId` and `chipcard` were ever rendered against a field —
     * `tarifGroupId` was not — so a prescription missing a tarif group showed a
     * dead Save button and no explanation anywhere. Naming the blocking fields
     * turns "nothing happens when I click save" into something answerable
     * without reading the source.
     *
     * Field labels rather than state keys, because this is read by reception.
     */
    const RX_FIELD_LABELS: Record<string, string> = {
      accountId: "Kostenträger",
      tarifGroupId: "Tarif Group",
      chipcard: "Chipkartennummer",
    };
    const saveBlockedReason = React.useMemo(() => {
      const blocking = Object.entries(rxerrors)
        .filter(([, error]) => error !== "")
        .map(([field, error]) => `${RX_FIELD_LABELS[field] ?? field}: ${error}`);
      return blocking.length ? `Speichern nicht möglich — ${blocking.join(", ")}` : "";
    }, [rxerrors]);

    const handleInputChange = (
      event: React.ChangeEvent<HTMLInputElement>,
      index?: any,
      idValue?: string
    ) => {
      const { name, value, checked, type, id } = event.target;
      setUnsavedChanges(true);

      // If the user types their own value into one of these fields, it's no
      // longer "from the patient", so remove its flag to hide the blue bar.
      if (name === "chipcard" || name === "insureeNumber" || name === "insuranceStatus") {
        setAppliedFields((prev) => {
          if (prev[name] === undefined) return prev;
          const next = { ...prev };
          delete next[name];
          return next;
        });
      }

      const inputValue = type === "checkbox" ? checked : value;

      if (name === "practiceNumber") {
        if (value.length > 9) return;
      }

      if (name === "doctorNumber") {
        if (value.length > 9) return;
        // Only update the field, do not clear doctor info here!
        setFormData((prevValues: any) => ({
          ...prevValues,
          doctorNumber: value,
        }));
        return;
      }

      if (name === "date" && formData.isPerpetual && value) {
        setFormData((prevValues: any) => ({
          ...prevValues,
          unvirtualize: true,
        }));
      }
      if (name === "therapy") {
        const updatedTherapyRxs = [...formData.therapyRxs];

        // MD-232: the line names the prescribed TERM as the text that was
        // typed, and the server resolves it text -> alias -> term on save.
        // Previously this looked the value up among tarif-bound BILLABLE LINE
        // ITEMS and wrote state only on a hit — so a real term like "KG-ZNS",
        // which is no line item's abbreviation, stored nothing at all and the
        // save reached the server naming no term ("Heilmittel unbekannt").
        // `abbreviation` is kept in step because the table cell reads it.
        // `isSupplementary` is preserved: it decides which row group the line
        // belongs to and is not a property of the typed text.
        if (value) {
          updatedTherapyRxs.forEach((therapy, i) => {
            if (therapy.id === idValue) {
              updatedTherapyRxs[i] = {
                ...updatedTherapyRxs[i],
                rawText: value,
                therapy: {
                  ...updatedTherapyRxs[i].therapy,
                  abbreviation: value,
                },
              };
            }
          });
        } else {
          // Cleared: drop the term as well as the display value.
          updatedTherapyRxs.forEach((therapy, i) => {
            if (therapy.id === idValue) {
              updatedTherapyRxs[i] = {
                ...updatedTherapyRxs[i],
                rawText: null,
                therapy: {
                  ...updatedTherapyRxs[i].therapy,
                  id: "",
                  abbreviation: "",
                  description: "",
                  price: "",
                },
              };
            }
          });
        }

        // Update form data with the new therapy data
        setFormData((prevFormData: any) => ({
          ...prevFormData,
          therapyRxs: updatedTherapyRxs,
        }));
      }

      // For "amount" input change
      if (name === "amount") {
        const updatedTherapyRxs = formData.therapyRxs.map((therapy: any) => {
          if (therapy.id === idValue) {
            // Match based on therapy.id
            return {
              ...therapy,
              amount: value, // Update the amount
              frequency: formData.therapyRxs[0].frequency || therapy.frequency,
            };
          }
          return therapy;
        });

        setFormData((prevFormData) => ({
          ...prevFormData,
          therapyRxs: updatedTherapyRxs,
        }));
      }

      // For "rxSeries" input change
      if (name === "rxSeries") {
        const updatedTherapyRxs = formData.therapyRxs.map((therapy: any) => {
          if (therapy.id === idValue) {
            // Match based on therapy.id
            return {
              ...therapy,
              rxSeries: { id: id, label: value },
            };
          }
          return therapy;
        });

        setFormData((prevFormData) => ({
          ...prevFormData,
          therapyRxs: updatedTherapyRxs,
        }));
      }

      // For "therapyFrequency" input change
      if (name === "therapyFrequency") {
        const updatedTherapyRxs = formData.therapyRxs.map((therapy: any) => ({
          ...therapy,
          frequency: {
            id: value, // Update the frequency id
            text: value, // Update the frequency text
          },
        }));

        setFormData((prevFormData) => ({
          ...prevFormData,
          therapyRxs: updatedTherapyRxs,
        }));
      }

      if (name.startsWith("icdCodeEntries")) {
        const inputElement = document.querySelector(`[name="${name}"]`) as
          | HTMLInputElement
          | HTMLTextAreaElement;
        const cursorPosition = inputElement?.selectionStart || 0;
        let updatedValue = value;
        let newCursorPosition = cursorPosition; // Initialize new cursor position

        // Check if the length is at least 4 and if a period needs to be inserted
        if (value.length >= 4) {
          // Check if the period already exists at the correct position
          if (!value.includes(".")) {
            // Insert a period after the third character if it's not already there
            updatedValue = value.slice(0, 3) + "." + value.slice(3);

            // Adjust cursor position if a period was inserted before the original cursor position
            if (cursorPosition > 3) {
              newCursorPosition = cursorPosition + 1; // Move cursor one position forward
            }
          }
        }

        // Update the form values with the correctly formatted value
        setFormData((prevValues: any) => ({
          ...prevValues,
          icdCodeEntries: prevValues.icdCodeEntries.map((icdEntry: any, i: number) => {
            return i === index
              ? { id: idValue || undefined, userText: updatedValue }
              : { id: icdEntry?.id, userText: icdEntry?.userText || "" };
          }),
        }));

        // Restore cursor position after the update
        setTimeout(() => {
          if (inputElement) {
            inputElement.selectionStart = inputElement.selectionEnd = newCursorPosition;
          }
        }, 0);
      }

      if (name === "rxtype") {
        // The Rezept-typ DECIDES the tarif group. Without this the group the Rx
        // was opened with survived the type change, so a type switched to
        // Privat here was still billed as GKV — and the therapy re-link below
        // resolved every line against that stale group.
        const selectedRxTypeId = String(id ? id : inputValue);
        const { nextTarifGroupId, requiresChoice } = resolveTarifGroupForRxType(
          safeMasterTablesData,
          selectedRxTypeId,
          formData?.tarifGroupId,
          accountTherapyType
        );
        setFormData((prevValues) => ({
          ...prevValues,
          diagnosegroup: null, // Clear diagnosegroup when rxtype changes
          tarifGroupId: nextTarifGroupId,
        }));
        if (requiresChoice) {
          notificationStore.showMessage(
            "Bitte wählen Sie eine Tarifgruppe für den gewählten Rezept-Typ.",
            "warning"
          );
        }
        filterDiagnoseGroups(selectedRxTypeId);
      }

      // if (name === "doctorNumber") {
      //   // Autopopulate doctor and bsnr based on LANR selection
      //   const selectedDoctor: any = doctorOptions.find(
      //     (doctor: any) => doctor.number === Number(value)
      //   );
      //   // setFetchedData(selectedDoctor);
      //   console.log("UZZHHHH", selectedDoctor);
      //   if (selectedDoctor) {
      //     setFormData((prevFormValues: any) => ({
      //       ...prevFormValues,
      //       doctorId: selectedDoctor.id,
      //       practiceNumber: selectedDoctor.clinicNumber,
      //       doctorData:selectedDoctor
      //     }));
      //     setSelectedDoctor(selectedDoctor.contactInfo.lastName);
      //     setSelectedBSNR(selectedDoctor.clinicNumber);
      //   }
      // }
      // Leitsymptomatik: the a/b/c/d codes and the patientenindividuelle flag are
      // independent — a Verordnung can require both, so neither clears the other.
      // Update form fields based on their names
      setFormData((prevValues: any) => ({
        ...prevValues,
        [name]: id ? id : inputValue,
      }));

      // if (name === "doctorNumber") {
      //   // Autopopulate doctor and bsnr based on LANR selection
      //   const selectedDoctor: any = doctorOptions.find(
      //     (doctor: any) => doctor.number === Number(value)
      //   );
      //   setFetchedData(selectedDoctor);
      //   console.log("UZZHHHH", selectedDoctor);
      //   if (selectedDoctor) {
      //     setFormData((prevFormValues: any) => ({
      //       ...prevFormValues,
      //       doctorId: selectedDoctor.id,
      //       practiceNumber: selectedDoctor.clinicNumber,
      //     }));
      //     setSelectedDoctor(selectedDoctor.contactInfo.lastName);
      //     setSelectedBSNR(selectedDoctor.clinicNumber);
      //   }
      // }
      if (insurer[0]) {
        if (name === "Receiptpayer") {
          // Autopopulate doctor and bsnr based on LANR selection

          const selectedPayer: any = insurer[0].find(
            (option: any) => option.id === id || option.ik === value
          );
          if (selectedPayer) {
            setFormData((prevFormValues: any) => ({
              ...prevFormValues,
              chipcard: selectedPayer?.ik,
              // tarifGroupId: selectedPayer?.tarifGroup?.id,
            }));
          }
        }

        // if (name === "chipcard") {
        //   const selectedPayer: any = insurer[0].find((option: any) => option.ik === value);
        //   console.log("UZZHHHH", selectedPayer);
        //   if (selectedPayer) {
        //     setFormData((prevFormValues: any) => ({
        //       ...prevFormValues,
        //       Receiptpayer: selectedPayer.id,
        //       tarifGroupId: selectedPayer?.tarifGroup?.id,
        //     }));

        //     setSelectedPayer(selectedPayer.name);
        //     console.log("selectedPayer", selectedPayer.name);
        //   }
        // }
      }

      setFormData((prevFormData) => {
        const newFormData = { ...prevFormData, [name]: value };
        debounceValidation(newFormData);
        return newFormData;
      });
    };
    const handleChipCardDataEntry = (event: React.KeyboardEvent | React.FocusEvent) => {
      rxvalidate();
      const chipCardValue = (event.currentTarget as HTMLInputElement).value;
      if (
        (event as React.KeyboardEvent).key === "Enter" ||
        event.type === "blur" ||
        event.type === "mouseleave"
      ) {
        // Check if the chipcard number is valid
        const selectedPayer = insurer[0]?.find((option: any) => option.ik === chipCardValue);
        if (selectedPayer) {
          // If valid, autofill payer info
          setFormData((prevFormValues: any) => ({
            ...prevFormValues,
            Receiptpayer: selectedPayer?.id,
            // tarifGroupId: selectedPayer?.tarifGroup?.id,
          }));
          setSelectedPayer(selectedPayer.name);
          notificationStore.showMessage(
            `${selectedPayer?.id} ist ein IK für ${selectedPayer?.name}`,
            "success"
          );
        } else if (chipCardValue) {
          // Show error if chipcard is invalid
          notificationStore.showMessage(
            "Bitte geben Sie eine gültige Chipkartennummer ein.",
            "warning"
          );
          // Pass all form values with updated chipcard value for validation
        }
      }
    };
    useEffect(() => {
      rxvalidate();
    }, [formData]);

    const [validationData, setValidationData] = useState<any>({});
    const debounceValidation = useCallback(
      debounce(async (currentFormData) => {
        const data = await getLiveValidation(currentFormData);
        setValidationData(data);
      }, 100),
      []
    );

    // ────────────────────────────────────────────────────────────────────────
    // "Aus Patientendaten übernehmen" card
    //
    // When an insurance field on the Rx is empty (or holds a value that differs
    // from the patient record), we surface it in a small card on top of the form.
    // The user can then explicitly copy each value into the Rx, instead of the value being filled in silently.
    // ────────────────────────────────────────────────────────────────────────
    const isEmptyValue = (value: unknown): boolean =>
      value === undefined || value === null || value === "";

    // Part 1 — Build the list of fields the card should offer.
    const insurancePrefillSuggestions = useMemo(() => {
      type PrefillSuggestion = {
        key: string;
        label: string;
        patientValue: string;
        secondary?: string;
        currentValue?: string;
        status: "missing" | "differs";
      };

      if (readOnlyView) return [] as PrefillSuggestion[];

      // Find the insurer (payer) that matches the patient's insurance number (IK),
      // so we can also show its name under the chipcard value.
      const matchedPayer = insurer[0]?.find(
        (option: any) => option.ik === patientValues?.payer?.ik
      );

      // List the 3 insurance fields, pairing the Rx value with the matching patient value.
      const fieldDefs: {
        key: string;
        rxValue: any;
        patientValue: any;
        secondary?: string;
      }[] = [
        {
          key: "chipcard",
          rxValue: formData?.chipcard,
          patientValue: patientValues?.payer?.ik,
          secondary: matchedPayer?.name ?? patientValues?.payer?.name ?? undefined,
        },
        {
          key: "insureeNumber",
          rxValue: formData?.insureeNumber,
          patientValue: patientValues?.insuranceNo,
        },
        {
          key: "insuranceStatus",
          rxValue: formData?.insuranceStatus,
          patientValue: patientValues?.status,
        },
      ];

      // Walk the fields and decide which ones to offer.
      const suggestions: PrefillSuggestion[] = [];
      for (const def of fieldDefs) {
        if (isEmptyValue(def.patientValue)) continue; // no patient value to offer

        // Suggest a field when it's empty ("missing") or has a different value
        // than the patient ("differs"). Skip fields that already match.
        let status: "missing" | "differs";
        if (isEmptyValue(def.rxValue)) {
          status = "missing";
        } else if (String(def.rxValue) !== String(def.patientValue)) {
          status = "differs";
        } else {
          continue;
        }

        // Add this field to the list the card will render.
        suggestions.push({
          key: def.key,
          label: INSURANCE_FIELD_LABELS[def.key],
          patientValue: String(def.patientValue),
          secondary: def.secondary,
          currentValue: status === "differs" ? String(def.rxValue) : undefined,
          status,
        });
      }
      return suggestions;
    }, [
      readOnlyView,
      formData?.chipcard,
      formData?.insureeNumber,
      formData?.insuranceStatus,
      patientValues,
      appliedFields,
    ]);

    // Part 2 — Copy ONE patient field into the Rx when its button is clicked.
    // For each field we: mark it as "from patient", write the value into
    // formData, and re-run validation.
    const applyPatientFieldToRx = (key: string) => {
      setUnsavedChanges(true);

      if (key === "chipcard") {
        const payer = patientValues?.payer;
        const ik = payer?.ik;
        if (isEmptyValue(ik)) return;

        const matchedPayer = insurer[0]?.find(
          (option: any) => option.ik === ik || option.id === payer?.id
        );

        // Mark chipcard as applied from patient (drives the blue bar).
        setAppliedFields((prev) => ({ ...prev, chipcard: true }));
        setFormData((prevValues: any) => {
          const nextValues = {
            ...prevValues,
            chipcard: ik,
            Receiptpayer: matchedPayer?.id ?? payer?.id ?? prevValues.Receiptpayer,
          };
          debounceValidation(nextValues);
          return nextValues;
        });
        setSelectedPayer(matchedPayer?.name ?? payer?.name ?? null);
        return;
      }

      if (key === "insureeNumber") {
        setAppliedFields((prev) => ({ ...prev, insureeNumber: true }));
        setFormData((prevValues: any) => {
          const nextValues = { ...prevValues, insureeNumber: patientValues?.insuranceNo ?? "" };
          debounceValidation(nextValues);
          return nextValues;
        });
        return;
      }

      if (key === "insuranceStatus") {
        setAppliedFields((prev) => ({ ...prev, insuranceStatus: true }));
        setFormData((prevValues: any) => {
          const nextValues = { ...prevValues, insuranceStatus: patientValues?.status ?? "" };
          debounceValidation(nextValues);
          return nextValues;
        });
      }
    };

    // Part 3 — Copy every offered field at once (the "Alle übernehmen" button).
    const applyAllPatientFields = () => {
      insurancePrefillSuggestions.forEach((suggestion) =>
        applyPatientFieldToRx(suggestion.key)
      );
    };

    // Part 4 — True when a field's value came from the patient (drives the blue bar):
    // either copied via the card (appliedFields) or auto-seeded on a new Rx.
    const isNewRx = !rxFormData?.rxNumber;
    const patientFieldValue: Record<string, string | undefined> = {
      chipcard: patientValues?.payer?.ik,
      insureeNumber: patientValues?.insuranceNo,
      insuranceStatus: patientValues?.status,
    };
    const isFromPatient = (key: string) => {
      if (readOnlyView) return false;
      if (appliedFields[key]) return true;
      const current = (formData as any)?.[key];
      return isNewRx && !isEmptyValue(current) && current === patientFieldValue[key];
    };

    // Blue bar shown on a field whose value originates from the patient, so the origin stays visible.
    const appliedFromPatientSx = (key: string) =>
      isFromPatient(key)
        ? {
            borderLeft: `3px solid ${mobiliTheme.palette.info.main}`,
            pl: "4px",
            borderRadius: "2px",
          }
        : {};
    const APPLIED_TOOLTIP = "Aus den Patientendaten übernommen.";

    // Wrap an insurance input to add the blue bar + tooltip when the value is from the patient.
    const renderPrefillField = (key: string, children: React.ReactNode) => {
      return (
        <Tooltip title={isFromPatient(key) ? APPLIED_TOOLTIP : ""} placement="bottom-start">
          <Box sx={appliedFromPatientSx(key)}>{children}</Box>
        </Tooltip>
      );
    };

    const handleGenericSubmit = async (event?: any) => {
      if (event) {
        setLoading(false);
        event.preventDefault(); // Prevent default form submission behavior
      }
      try {
        if (rxvalidate()) {
          // The doctor must actually be registered at the practice the Rx names:
          // the stamp takes its telephone number from the PRACTICE, so a
          // mismatched pair prints a stranger's number on a document that leaves
          // the building. Asked here rather than on a field, because the Arzt-Nr
          // check only fires when that field loses focus - correcting the BSNR
          // afterwards, or re-saving an Rx loaded from the database, slips past it.
          if (!(await confirmDoctorClinicMismatch(currentDoctor, currentClinic))) {
            return;
          }
          setLoading(true);
          const updatedRxValues: any = {
            ...formData,
            // `a`-`d` are boolean columns and `date` is a date column, both
            // nullable — but the form holds "" for anything it was not given,
            // and Postgres rejects "" outright rather than reading it as
            // "not stated". The Rx then never saves and the dialog closes,
            // taking the entry with it.
            //
            // Empty is ordinary here: of 15,365 prescriptions in production
            // 4,030 carry no date and 3,737 no `a`. Planning before the paper
            // prescription arrives is the normal workflow.
            //
            // Seen first on the Blankoverordnung (MD-514 follow-up). These
            // three forms carry the identical initial values and the identical
            // `{...formData}` spread — they survive only when the Leitsymptom
            // effect happens to run, which it does not when no Diagnosegruppe
            // is chosen. Fixing them together so the fault cannot simply move.
            a: emptyToNull(formData.a),
            b: emptyToNull(formData.b),
            c: emptyToNull(formData.c),
            d: emptyToNull(formData.d),
            date: emptyToNull(formData.date),
            id: rxFormData?.id || "",
            rxNumber: rxFormData?.rxNumber || "",
            LANR: formData.doctorNumber || "",
            BSNR: formData.practiceNumber || "",
            // Save the Rx-native insurance values as-is. Values are only copied
            // from the patient when the user explicitly clicks "Übernehmen" in the
            // prefill card (applyPatientFieldToRx), which updates formData first.
            insureeNumber: formData?.insureeNumber ?? "",
            insuranceStatus: formData?.insuranceStatus ?? "",
            // doctor: formData.doctorData,
            accountId:
              account[0].find((option: any) => option.name === formData.accountId)?.id ||
              formData.accountId,
            rxtype:
              rxType[0].find((option: any) => option.text === formData.rxtype)?.id ||
              formData.rxtype ||
              "",
            // An FK column: "" is not a missing value to Postgres, it is an id
            // that does not exist. Guarded like the fields above.
            diagnosegroup: emptyToNull(
              diagnoseGroups[0].find((option: any) => option.code === formData.diagnosegroup)
                ?.id || formData.diagnosegroup
            ),

            Receiptpayer:
              insurer[0].find((option: any) => option.id === formData.chipcard)?.id ||
              formData.Receiptpayer,
            // therapyRxs:
            //   formData.therapyRxs
            //     ?.filter((therapy: any) => therapy.therapy.id !== "") // Filter out entries without therapy.therapyId or therapy.id
            //     .map((therapy: any) => ({

            //       frequency: therapy.frequency || "",

            //     })) || [],

            therapyRxs: (formData.therapyRxs || []) // Ensure formData.therapyRxs is an array or provide an empty array
              // MD-232: a row is real when it NAMES A TERM. Gating on
              // `therapy.id` dropped every freshly entered row (see rowNamesTerm).
              .filter(rowNamesTerm)
              .map((therapy: any) => {
                if (
                  !(therapy.frequency && therapy.frequency.id) ||
                  !rowNamesTerm(therapy) ||
                  !therapy.amount
                ) {
                  throw new Error(
                    "Bitte fügen Sie die Behandlung hinzu, um das Rezept zu speichern/bearbeiten (Therapie, Menge, Häufigkeit sind Pflichtfelder) und deaktivieren Sie auch die Zelle nach der Bearbeitung!"
                  );
                }
                const validTherapy = validateTherapy(
                  rowTermText(therapy),
                  safeMasterTablesData,
                  termOptions
                );
                const validFrequency = validateFrequency(
                  therapy.frequency.id,
                  safeMasterTablesData
                );
                if (!validTherapy || !validFrequency) {
                  throw new Error("Bitte geben Sie eine gültige Therapie und Häufigkeit ein.");
                }
                const insurer = safeMasterTablesData
                  .filter((obj: any) => "insurer" in obj)
                  .map((obj: any) => obj.insurer); // Extract the insurer array

                const payer =
                  insurer[0].find((option: any) => option.name === formData.Receiptpayer)?.id ||
                  formData.Receiptpayer;
                const insurerInfo = insurer[0]?.filter((obj: any) => obj.id === payer)[0]
                  ?.tarifGroup?.id; //To check with Nafees why is it used
                // const therapyName = therapy.therapy.abbreviation.toUpperCase();
                // console.log("TherapyName SEEEEEEEEEEEEEe", therapyName);
                const tarifTherapy = getTherapyId(
                  safeMasterTablesData,
                  formData.tarifGroupId,
                  therapy.therapy.abbreviation
                );

                return {
                  ...therapy,
                  therapy: { id: tarifTherapy, abbreviation: therapy.therapy.abbreviation },
                };
              }),
          };

          // The doctor/clinic state is the single source of truth for the Rx's doctor & clinic.
          // Always derive doctorId/clinicId/LANR/BSNR from currentDoctor/currentClinic
          // to avoid stale formData values from overriding the actual linked entities.
          if (currentDoctor) {
            updatedRxValues.doctorId = (currentDoctor as any).id || formData.doctorId;
            updatedRxValues.LANR = (currentDoctor as any).LANR || formData.doctorNumber || "";
          } else {
            updatedRxValues.doctor = null;
            updatedRxValues.doctorId = null;
            updatedRxValues.doctorNumber = "";
            updatedRxValues.LANR = "";
          }
          if (currentClinic) {
            updatedRxValues.clinicId = currentClinic.id || formData.clinicId;
            updatedRxValues.BSNR = (currentClinic as any).BSNR || formData.practiceNumber || "";
          } else {
            updatedRxValues.clinic = null;
            updatedRxValues.clinicId = null;
            updatedRxValues.practiceNumber = "";
            updatedRxValues.BSNR = "";
          }
          // // Handle form submission logic here
          // const updatedFormData = {

          //   patient:{
          //     ...patientValues,
          //   firstName: formData.patient?.firstName || "",
          //   lastName: formData.patient?.lastName || "",
          //   dob: formData.patient?.dob || ""},
          //   rxs: updatedRxValues
          // };
           setFormData(updatedRxValues);
          if (entryType === EntryType.newRx && handleNewFormData) {
            handleNewFormData(updatedRxValues);

            //This for newRx setting rx form
            // mainFormSubmit()
          } else {
            patientSaveRxs(
              { patientId: patientId!, rxs: [updatedRxValues] },
              {
                onSuccess: () => {
                  // Only now is the form genuinely saved. This used to be set
                  // before the request went out, so a failed save left the form
                  // claiming no unsaved changes and Abbrechen discarded the
                  // user's edits without the usual warning.
                  setUnsavedChanges(false);
                  if (onRxSave && selectedRxId) {
                    onRxSave(selectedRxId);
                  }
                  onCloseRx();
                },
                // The global MutationCache already shows the server's message;
                // the dialog stays open, with the edits intact, so the user can
                // correct and retry rather than losing the form.
                onError: () => {
                  setUnsavedChanges(true);
                },
              }
            );
          }
         
          // await saveRx(updatedRxValues);

          //  await searchPatient({ id: patientValues.id });
        } else {
          notificationStore.showMessage(
            "Bitte füllen Sie alle erforderlichen Felder aus",
            "warning"
          );
        }
      } catch (error: any) {
        console.error("Error saving Rx:", error);
        notificationStore.showMessage(
          error.message || "Fehler beim Speichern des Rezepts",
          "error",
          error
        );
      } finally {

        // Set loading back to false when the API call is complete
        setLoading(false);
      
      }
    };

    const [therapyRxs, setTherapyRxs] = useState(formData.therapyRxs); // State to manage therapyRxs array
    const componentRef = useRef(null);
    const handleDuplicateRow = (
      index: number,
      event: React.ChangeEvent<HTMLInputElement>,
      idValue?: string
    ) => {
      const { value } = event.target;
      setUnsavedChanges(true);

      const updatedTherapyRxs = formData.therapyRxs.map((therapy: any, index: number) => {
        // Check if the current therapy matches the ID we're updating
        if (therapy.id === idValue) {
          // No even-amount gate: nothing splits the amount in half any more
          // (that was the old shadow-line model). Odd totals with doubling are
          // legal — e.g. 7 units at 2/day = 3 double days + 1 single.
          const updatedTherapy = {
            ...therapy,
            isDoubled: value as any,
            unitsPerDay: value ? 2 : 1,
            amount: therapy.amount,
          };

          // Check for duplicate therapies
          const isDuplicate = formData.therapyRxs.some((othertherapy, otherIndex) => {
            if (otherIndex !== index) {
              // Compare relevant properties
              return updatedTherapy.therapy.abbreviation === othertherapy.therapy.abbreviation;
            }
            return false;
          });

          if (isDuplicate) {
            notificationStore.showMessage(
              "Die Behandlung ist bereits beendet, so dass kein Duplikat zulässig ist.",
              "warning"
            );
            // Return the therapy as is to avoid changes
            return therapy;
          }

          // Return the updated therapy if all conditions are met
          return updatedTherapy;
        }

        // Return the therapy as is if the ID doesn't match
        return therapy;
      });

      // Update the formData state with the updated therapyRxs
      setFormData((prevFormData) => ({
        ...prevFormData,
        therapyRxs: updatedTherapyRxs,
      }));

    };

    const [filteredDiagnoseGroups, setFilteredDiagnoseGroups] = useState([]);
    // const [isDoctorNumberDisabled, setIsDoctorNumberDisabled] = useState(true);
    const [doesClinicExist, setDoesClinicExist] = useState(false);
    const [isClinicModalOpen, setIsClinicModalOpen] = useState(false);
    const [isClinicModalEditMode, setIsClinicModalEditMode] = useState(false);
    const [currentClinicId, setCurrentClinicId] = useState("");
    const [creationResponse, setCreationResponse] = useState<any>({
      id: "",
      clinics: [],
      contactInfo: {},
      doctorId: "",
      clinicId: "",
    });
    const [currentDoctorId, setCurrentDoctorId] = useState("");
    const [doesDoctorExist, setDoesDoctorExist] = useState(false);
    const [currentClinic, setCurrentClinic] = useState<ClinicForModal | undefined>();
    const [showClinicData, setShowClinicData] = useState(false);
    const [currentDoctor, setCurrentDoctor] = useState<Doctor | ClinicForModal>();
    const [autocompleteSearchTerm, setAutocompleteSearchTerm] = useState("");

    useEffect(() => {
      setCurrentDoctor(rxFormData?.doctor as any);
      setCurrentClinic(rxFormData?.clinic as any);
    }, [rxFormData?.doctor, rxFormData?.clinic]);

    // Keep display fields and FK IDs in sync with the actual doctor/clinic
    useEffect(() => {
      if (currentDoctor) {
        const lanr = (currentDoctor as any).LANR || "";
        setFormData((prev) => ({ ...prev, doctorNumber: lanr, doctorId: (currentDoctor as any).id || prev.doctorId }));
      }
    }, [currentDoctor]);

    useEffect(() => {
      if (currentClinic) {
        const bsnr = (currentClinic as any).BSNR || "";
        setFormData((prev) => ({ ...prev, practiceNumber: bsnr, clinicId: currentClinic.id || prev.clinicId }));
      }
    }, [currentClinic]);

    useEffect(() => {
      if (creationResponse && creationResponse.doctorId && creationResponse.clinicId) {
        setCurrentDoctor({
          id: creationResponse.doctorId,
          LANR: creationResponse.LANR,
          contactInfo: creationResponse.contactInfo as any,
          clinics: creationResponse.clinics,
        });
        const matchedClinic = (creationResponse.clinics || []).find((clinic: any) => clinic.id === creationResponse.clinicId);
        setCurrentClinic(matchedClinic as any || undefined);
        setFormData((prev) => ({
          ...prev,
          doctorId: creationResponse.doctorId || "",
          clinicId: creationResponse.clinicId || "",
          doctorNumber: creationResponse.LANR || "",
          practiceNumber: matchedClinic?.BSNR || "",
        }));
      }
    }, [creationResponse]);

    // useEffect(() => {
    //     setCurrentDoctor(rxFormData?.doctor);
    //     setCurrentClinic(rxFormData?.clinic);

    //     if (creationResponse?.id) {
    //       setFormData((prevFormValues: any) => ({
    //         ...prevFormValues,
    //         doctorNumber: creationResponse.id,
    //       }));
    //     }
    //   }, [creationResponse?.id, rxFormData?.clinic, rxFormData?.doctor]);

    const filterDiagnoseGroups = (rxtype: any) => {
      let typeForDGs = rxtype;
      if (rxtype === "Entlassmanagement") {
        typeForDGs = "Kasse";
      }
      const filtered = diagnoseGroups[0]?.filter(
        (option: any) =>
          option.RxType?.id === typeForDGs &&
          (!option.therapyType || option.therapyType === accountTherapyType)
      );
      setFilteredDiagnoseGroups(filtered);
    };

    useEffect(() => {
      filterDiagnoseGroups(formData.rxtype);
    }, [formData.rxtype, accountTherapyType]);

    const handlePracticeNumberInteraction = async (
      e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) => {
      const value = (e.target as HTMLInputElement).value;
      const isKeyboardEvent = e.type === "keydown";
      const isTriggerKey =
        (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === "Tab";

      if (isKeyboardEvent && isTriggerKey && value.length !== 0 && value.length !== 9) {
        e.preventDefault();
        notificationStore.showMessage("BSNR muss 9-stellig sein", "error");
        return;
      }

      if (
        (e.type === "blur" || (isTriggerKey && isKeyboardEvent)) &&
        (value.length === 9 || value.length === 0)
      ) {
        try {
          if (value.length === 9) {
            const clinicData = (await getClinicById(value)) as ClinicForModal;
            if (clinicData) {
              setDoesClinicExist(true);
              setCurrentClinic(clinicData);
              setFormData((prev) => ({ ...prev, clinicId: clinicData.id }));
              setShowClinicData(true);
            } else {
              setDoesClinicExist(false);
              setCurrentClinicId(value);
              setCurrentDoctorId(currentDoctor?.id || "");
              setShowClinicData(false);
              setCurrentClinic(undefined);
              setIsClinicModalOpen(true); // Open dialog if not found
            }
          } else if (value.length === 0) {
            setCurrentClinic(undefined);
          }
        } catch (error) {
          console.error("Error fetching clinic:", error);
        }
      }
    };

    // Doctor Number (LANR) handler
    const handleGetDoctorById = async (
      e: React.FocusEvent<HTMLInputElement> | React.KeyboardEvent<HTMLInputElement>
    ) => {
      const value = (e.target as HTMLInputElement).value;
      const isKeyboardEvent = e.type === "keydown";
      const isTriggerKey =
        (e as React.KeyboardEvent).key === "Enter" || (e as React.KeyboardEvent).key === "Tab";

      if (isKeyboardEvent && isTriggerKey && value.length !== 0 && value.length !== 9) {
        e.preventDefault();
        notificationStore.showMessage("LANR muss 9-stellig sein", "error");
        return;
      }

      if (
        (e.type === "blur" || (isTriggerKey && isKeyboardEvent)) &&
        (value.length === 9 || value.length === 0)
      ) {
        try {
          if (value.length === 9) {
            const doctorData: any = await getDoctorById(value);
            if (doctorData) {
              setDoesDoctorExist(true);
              setCurrentDoctor(doctorData);
              setFormData((prev) => ({ ...prev, doctorId: doctorData.id }));

              // Only check association if currentClinic is set
              if (currentClinic && currentClinic.id) {
                const isDoctorInClinic = doctorData.clinics?.some(
                  (clinic: any) => clinic.id === currentClinic.id
                );

                if (isDoctorInClinic) {
                  notificationStore.showMessage(
                    "This doctor exists and is already associated with this clinic",
                    "success"
                  );
                } else {
                  const confirmed = await confirmDoctorClinicJoin(doctorData, currentClinic);
                  if (!confirmed) {
                    // User cancelled — clear the doctor selection (treat as a mistake).
                    setCurrentDoctor(undefined);
                    setDoesDoctorExist(false);
                    setFormData((prev) => ({ ...prev, doctorId: "", doctorNumber: "" }));
                    return;
                  }
                  notificationStore.showMessage(
                    "This doctor exists already and will now get associated with this clinic",
                    "success"
                  );
                  handleAssociateDoctorToClinic(doctorData);
                }
              } else {
                // No clinic entered yet, just set doctor, do not associate
                notificationStore.showMessage(
                  "Bitte geben Sie zuerst eine gültige Betriebsstätten-Nr. (BSNR) ein.",
                  "warning"
                );
              }
            } else {
              // Doctor does not exist at all, open dialog to create new
              setDoesDoctorExist(false);
              setIsClinicModalOpen(true);
              setCurrentDoctorId(value);
              setCurrentClinicId(currentClinic?.id || "");
            }
          } else if (value.length === 0) {
            setCurrentDoctor(undefined);
          }
        } catch (error) {}
      }
    };
    enum ReportMethod {
      Email = "Email",
      Fax = "Fax",
      Post = "Post",
    }

    const handleAssociateDoctorToClinic = async (doctorData: any) => {
      const createDoctorClinicDto = {
        id: doctorData?.id || "",
        contactInfo: {
          salutation: doctorData?.contactInfo?.salutation || "",
          firstNames: doctorData?.contactInfo?.firstNames || "",
          lastName: doctorData?.contactInfo?.lastName || "",
        },
        clinics: [
          {
            id: currentClinic?.id || "",
            contactInfo: {
              businessName: currentClinic?.contactInfo?.businessName || "",
              phone1: currentClinic?.contactInfo?.phone1 || "",
              fax: currentClinic?.contactInfo?.fax || "",
              addressLine1: currentClinic?.contactInfo?.addressLine1 || "",
              addressLine2: currentClinic?.contactInfo?.addressLine2 || "",
              city: currentClinic?.contactInfo?.city || "",
              zip: currentClinic?.contactInfo?.zip || "",
              email: currentClinic?.contactInfo?.email || "",
            },
            notes: currentClinic?.notes || "",
            methodForDrReport: (currentClinic?.methodForDrReport as ReportMethod) || "Email",
            methodForRxOrder: (currentClinic?.methodForRxOrder as ReportMethod) || "Email",
          },
        ],
      };

      // Call createDoctorWithClinic to save data
      // setLoading(true);
      try {
        const response = await createDoctorWithClinic(createDoctorClinicDto);
        return response;
      } catch (error) {
        console.error("Error saving doctor and clinic:", error);
      } finally {
        setLoading(false);
      }
    };

    const FormContent = (
      <Grid
        container
        direction={"row"}
        spacing={1}
        sx={{ paddingTop: "0px", paddingLeft: "2px", border: "0px dashed blue" }}
      >
        {!readOnlyView ? (
          <Grid item xs={12} md={3} sx={{ border: "0px dashed green" }}>
            {insurancePrefillSuggestions.length > 0 && (
              <FlatCard
                title="Aus Patientendaten"
                sx={{ mb: 1 }}
                sxContent={{ p: 1 }}
              >
                <Typography
                  sx={{ fontSize: "11px", color: "text.secondary", lineHeight: 1.2, mb: 1 }}
                >
                  Werte aus den Patientendaten übernehmen:
                </Typography>
                {insurancePrefillSuggestions.map((suggestion) => (
                  <Box
                    key={suggestion.key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 0.5,
                      "&:not(:last-of-type)": { mb: 0.5 },
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: "10px", color: "text.secondary", lineHeight: 1.1 }}
                      >
                        {suggestion.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {suggestion.patientValue}
                      </Typography>
                      {suggestion.secondary && (
                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {suggestion.secondary}
                        </Typography>
                      )}
                      {suggestion.status === "differs" && (
                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: attentionColors.immediateAttention,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Aktuell: {suggestion.currentValue}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip
                      title={
                        suggestion.status === "differs"
                          ? "Mit Patientenwert überschreiben"
                          : "Übernehmen"
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => applyPatientFieldToRx(suggestion.key)}
                        sx={{ color: mobiliTheme.palette.info.main, p: 0.25, flexShrink: 0 }}
                      >
                        <StartIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
                {insurancePrefillSuggestions.length >= 2 && (
                  <Button
                    fullWidth
                    size="small"
                    variant="text"
                    startIcon={<StartIcon sx={{ fontSize: 14 }} />}
                    onClick={applyAllPatientFields}
                    sx={{ mt: 0.5, fontSize: "10px", textTransform: "none", py: 0.25 }}
                  >
                    Alle übernehmen
                  </Button>
                )}
              </FlatCard>
            )}
            <FlatCard title={`Rezept ${rxFormData?.rxNumber || ""}`}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controls.Select
                    name="rxtype"
                    label="Rezept-typ"
                    value={formData?.rxtype}
                    onChange={handleInputChange}
                    options={rxType[0]?.map((option: any) => ({
                      label: option?.text.toString(),
                      value: option?.text.toString(),
                      id: option?.id.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    id={1}
                    name="accountId"
                    label="Mandant"
                    value={formData?.accountId}
                    onChange={handleInputChange}
                    options={accounts?.map((option: any) => ({
                      label: option?.name?.toString(),
                      value: option?.name?.toString(),
                      id: option?.id?.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                    required
                    error={rxerrors.accountId}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    name="status"
                    label="Rezeptverbleib"
                    value={formData?.status}
                    onChange={handleInputChange}
                    options={[
                      {
                        label: "noch nicht erstellt",
                        value: "noch nicht erstellt",
                      },
                      { label: "beim Patient", value: "beim Patient" },
                      { label: "beim Therapeut", value: "beim Therapeut" },
                      { label: "vorhanden", value: "vorhanden" },
                    ]}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    name="tarifGroupId"
                    label="Tarif Group"
                    id={1}
                    value={formData?.tarifGroupId}
                    onChange={handleInputChange}
                    options={tarifGroupOptions.map((value: any) => ({
                      label: value.toString(),
                      value: value.toString(),
                    }))}
                    disabled={readOnlyView}
                    style={{
                      width: "100%",
                      marginTop: "0px",
                      marginBottom: "0px",
                      fontSize: "10px",
                    }}
                    required
                    // `rxvalidate` marks this "Pflichtfeld" and that error
                    // disables Speichern — but the field never showed it, so a
                    // new Rx with no tarif group presented a dead Save button
                    // and no reason anywhere on screen.
                    error={rxerrors.tarifGroupId}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Checkbox
                    name="isPerpetual"
                    label="Dauerrezept"
                    value={formData?.isPerpetual}
                    onChange={handleInputChange}
                    disabled={readOnlyView}
                  />
                </Grid>
              </Grid>
            </FlatCard>

            <DoctorCard
              doctor={currentDoctor}
              clinic={currentClinic}
              formData={{
                doctorNumber: formData.doctorNumber,
                signatureStatus: formData.signatureStatus,
                clinic: currentClinic,
              }}
              validationData={validationData}
              showSignatureCheck={!readOnlyView}
              readOnlyView={readOnlyView}
              onInputChange={handleInputChange}
              onEdit={() => {
                setCurrentClinicId(currentClinic?.id || "");
                setCurrentDoctorId(currentDoctor?.id || (currentDoctor as any)?.id || "");
                setIsClinicModalEditMode(true);
                setIsClinicModalOpen(true);
              }}
              onRemoveDoctor={() => {
                setCurrentDoctor(undefined);
                setCurrentDoctorId("");
                setFormData((prev) => ({
                  ...prev,
                  doctor: undefined,
                  doctorId: "",
                  doctorNumber: "",
                  LANR: "",
                }));
              }}
              onRemoveClinic={() => {
                setCurrentClinic(undefined);
                setCurrentClinicId("");
                setFormData((prev) => ({
                  ...prev,
                  clinic: undefined,
                  clinicId: "",
                  practiceNumber: "",
                  BSNR: "",
                }));
              }}
              onMigrationComplete={(type, newValue) => {
                if (type === "doctor") {
                  setFormData((prev) => ({ ...prev, doctorNumber: newValue }));
                  const doctorPK = (currentDoctor as any)?.id;
                  if (doctorPK) getDoctorById(doctorPK).then((doc) => { if (doc) setCurrentDoctor(doc as any); });
                } else {
                  setFormData((prev) => ({ ...prev, practiceNumber: newValue }));
                  const clinicPK = currentClinic?.id;
                  if (clinicPK) getClinicById(clinicPK).then((cli) => { if (cli) setCurrentClinic(cli as any); });
                }
              }}
            />

            <FlatCard
              title=" Heilmittel nach Maßgabe des Kataloges"
              sx={{ width: "500px", marginTop: "100px" }}
            >
              <Table
                sx={{
                  borderCollapse: "collapse",
                  width: "100%",
                  border: "1px solid #ccc",
                  background: "white",
                }}
              >
                <TableBody sx={{ maxHeight: "50px", overflowY: "scroll" }}>
                  <TableCell>Aktionen</TableCell>
                  <TableCell>Heilmittel</TableCell>
                  <TableCell></TableCell>
                  <TableCell>Menge</TableCell>
                  <TableCell>Series</TableCell>
                  {formData?.therapyRxs
                    ?.filter((therapy) => !therapy.therapy.isSupplementary) // Exclude supplementary therapies
                    .map((therapy, rowIndex) => (
                      <TableRow
                        key={rowIndex}
                        // style={{ border: "1px solid red", height: "1px" }}
                      >
                        <TableCell sx={{ padding: "0px", width: "50px", height: "1px" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTherapy(therapy.id)}
                            disabled={readOnlyView} // Prevent deletion in read-only mode
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "50px",
                            height: "1px",
                          }}
                        >
                          <TherapyTermField
                            therapyType={accountTherapyType}
                            disabled={readOnlyView}
                            name="therapy"
                            label=""
                            value={therapy.therapy.abbreviation}
                            onChange={(event: any) =>
                              handleInputChange(event, rowIndex, therapy?.id)
                            }
                            // options={therapyOptions[0]
                            //   ?.filter((therapyObj: any) => therapyObj.duration)
                            //   ?.map((therapy: any) => ({
                            //     label: therapy?.abbreviation?.toString(),
                            //     value: therapy?.abbreviation?.toString(),
                            //   }))}
                            style={{
                              width: "100px",
                              marginTop: "0px",
                              marginBottom: "0px",
                              fontSize: "10px",
                            }}
                          />
                        </TableCell>
                        {/* <TableCell
                            sx={{
                              padding: "0px",
                              direction: "column",
                              height: "1px",
                            }}
                          >
                            <Typography sx={{ fontSize: "10px" }}>
                              {therapy.therapy.description}
                            </Typography>
                          </TableCell> */}
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "10px",
                            height: "1px",
                          }}
                        >
                          <Controls.Checkbox
                            name="doubled"
                            value={therapy.isDoubled}
                            label={
                              <Typography sx={{ fontSize: "10px" }}>Doppelbehandlung</Typography>
                            }
                            disabled={!therapy.therapy.abbreviation || readOnlyView}
                            onChange={(event: any) => {
                              handleDuplicateRow(rowIndex, event, therapy.id);
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "50px",
                            height: "1px",
                            borderLeft: "1px solid #ccc",
                          }}
                        >
                          <Controls.Input
                            name="amount"
                            disabled={readOnlyView}
                            label=""
                            value={therapy.amount}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              handleInputChange(event, rowIndex, therapy.id);
                            }} // Pass rowIndex to handleInputChange
                            style={{ width: "100%", margin: "0px" }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "80px",
                            height: "1px",
                            borderLeft: "1px solid #ccc",
                          }}
                        >
                          <Controls.Select
                            name="rxSeries"
                            // label="Rezeptverbleib"
                            id={1}
                            style={{ width: "100%", margin: "0px" }}
                            value={therapy.rxSeries?.label}
                            onChange={(event: any) =>
                              handleInputChange(event, rowIndex, therapy.id)
                            }
                            options={savedSeriesForPatient.map((option: any) => ({
                              label: option.label,
                              value: option.label,
                              id: option.id,
                            }))}
                            onBlur={true}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              Ergänzendes Heilmittel
              <Table
                sx={{
                  borderCollapse: "collapse",
                  width: "100%",
                  border: "1px solid #ccc",
                  background: "white",
                }}
              >
                <TableBody sx={{ maxHeight: "20px", overflowY: "scroll" }}>
                  <TableCell>Aktionen</TableCell>
                  <TableCell>Heilmittel</TableCell>
                  <TableCell></TableCell>
                  <TableCell>Menge</TableCell>
                  {formData?.therapyRxs
                    ?.filter((therapy) => therapy.therapy.isSupplementary)
                    .map((therapy: any, supplementaryIndex) => (
                      <TableRow
                        key={therapy.id}
                        // style={{ border: "1px solid red", height: "1px" }}
                      >
                        <TableCell sx={{ padding: "0px", width: "50px", height: "1px" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteTherapy(therapy.id)}
                            disabled={readOnlyView} // Prevent deletion in read-only mode
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "50px",
                            height: "1px",
                          }}
                        >
                          <TherapyTermField
                            therapyType={accountTherapyType}
                            isSupplementary
                            disabled={readOnlyView}
                            name="therapy"
                            value={therapy?.therapy?.abbreviation || ""} // Default empty if no therapy
                            onChange={(event: any) =>
                              handleInputChange(event, supplementaryIndex, therapy?.id)
                            }
                            style={{
                              width: "110px",
                              marginTop: "0px",
                              marginBottom: "0px",
                              fontSize: "10px",
                            }}
                          />
                        </TableCell>
                        {/* <TableCell
                            sx={{
                              padding: "0px",
                              direction: "column",
                              height: "1px",
                            }}
                          >
                            <Typography sx={{ fontSize: "10px" }}>
                              {therapy.therapy.description}
                            </Typography>
                          </TableCell> */}
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "10px",
                            height: "1px",
                          }}
                        >
                          <Controls.Checkbox
                            name="doubled"
                            value={therapy?.isDoubled || false}
                            label={
                              <Typography sx={{ fontSize: "10px" }}>Doppelbehandlung</Typography>
                            }
                            disabled={!therapy?.therapy?.abbreviation || readOnlyView}
                            onChange={(event: any) =>
                              handleDuplicateRow(supplementaryIndex, event, therapy?.id)
                            }
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "0px",
                            width: "50px",
                            height: "1px",
                            borderLeft: "1px solid #ccc",
                          }}
                        >
                          <Controls.Input
                            name="amount"
                            value={therapy?.amount || ""}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              handleInputChange(event, supplementaryIndex, therapy?.id)
                            }
                            disabled={readOnlyView}
                            style={{ width: "100%", margin: "0px" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </FlatCard>

            {/* <Controls.Input
              name="practiceNumber"
              label="BSNR"
              value={selectedBSNR || formData?.practiceNumber}
              onChange={handleInputChange}
            /> */}
          </Grid>
        ) : (
          <Grid item xs={12} md={4} sx={{ border: "0px dashed green" }}>
            <FlatCard title="Allgemein" sx={{ marginBottom: "2px" }}>
              <Grid container spacing={0} justifyContent="space-between">
                {/* First Row */}
                <Grid item xs={5}>
                  <Typography textAlign="left" sx={{ fontSize: "0.8em" }}>
                    Rezept-Typ:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Controls.Chip
                    label={formData.rxtype || "-"}
                    sx={{ width: "100%", fontSize: "0.7rem" }}
                  />
                </Grid>

                {/* Second Row */}
                <Grid item xs={5}>
                  <Typography textAlign="left" style={{ fontSize: "0.8rem" }}>
                    Mandant:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Controls.Chip
                    label={formData.accountId || "-"}
                    sx={{ width: "100%", fontSize: "0.7rem", padding: "0px" }}
                  />
                </Grid>

                {/* Third Row */}
                <Grid item xs={5}>
                  <Typography textAlign="left" style={{ fontSize: "0.8rem" }}>
                    Rx Location:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Controls.Chip
                    label={
                      formData.isHomeVisit === true
                        ? "Haus/Heim"
                        : formData.isHomeVisit === false
                        ? "Praxis"
                        : "-"
                    }
                    sx={{ width: "100%", fontSize: "0.7rem" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Select
                    name="processingStatus"
                    label="Status"
                    onChange={updateRxProcessingStatus}
                    value={formData?.processingStatus || RxDtoProcessingStatusEnum.InBehandlung}
                    options={Object.values(RxDtoProcessingStatusEnum).map((value) => ({
                      value: value,
                      label: value,
                    }))}
                    noTriggerChange={true}
                    // style={{
                    //   width: "100%",
                    //   marginTop: "0px",
                    //   marginBottom: "0px",
                    //   fontSize: "10px",
                    // }}
                  />
                </Grid>
              </Grid>
            </FlatCard>

            {rxFormData && (
              <FlatCard 
                title="Validierung" 
                sxContent={{ 
                  p: 0, 
                  overflow: "hidden",
                  "&:last-child": { 
                    paddingBottom: 1 
                  }
                }}
              >
                <Box 
                  sx={{ 
                    maxHeight: "400px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    margin: 0,
                    "&::-webkit-scrollbar": {
                      width: "4px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#888",
                      borderRadius: "2px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#555",
                    },
                  }}
                >
                  <ValidationCard
                    patientData={patientValues}
                    selectedRx={formData}
                    form={formType.SHORT}
                  />
                </Box>
              </FlatCard>
            )}

            <FlatCard
              title="Rezepthinweise"
              sxContent={{ p: 0.75 }}
              sx={{
                paddingTop: "5px",
                marginBottom: "8px",
                textAlign: "left",
              }}
            >
              <Notes notes={rxNotes} setNotes={updateNote} maxVisibleNotes={3} noteKind="Rezepthinweis" />
            </FlatCard>

            <DoctorCard
              doctor={currentDoctor}
              clinic={currentClinic}
              formData={{
                doctorNumber: formData?.doctorNumber,
                clinic: formData?.clinic,
              }}
              showSignatureCheck={false}
              readOnlyView={true}
            />

            
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: "flex", border: "0px solid red", borderColor: "transparent" }}
        >
          {" "}
          {/* transparent border color is a hack because i couldnt figure out any way to add top padding*/}
          <ScalableComponentSimple occupiedHeight={readOnlyView ? 0.8 : 1}>
            <Container
              sx={{ border: "0px solid yellow" }}
              className={classes.container}
              ref={componentRef}
            >
              {/* Small box */}
              <form onSubmit={handleGenericSubmit} className={classes.form}>
                <Grid container spacing={1}>
                  <Grid item sx={{ border: "0px solid green" }}>
                    {/* Header region — native CSS grid with fixed inch tracks
                        (see .headerGrid). The Zuzahlung/Unfall/BVG checkboxes
                        sit in the paper's ACTUAL left margin (container
                        padding) as white boxes with their labels INSIDE,
                        exactly like the printed Muster 13; checking stamps a
                        ✕ across the box. */}
                    <div className={classes.headerGrid}>
                      <div className={classes.marginRail}>
                        <div
                          role="checkbox"
                          aria-checked={formData?.isCopaymentExempted === true}
                          aria-label="Zuzahlungsfrei"
                          className={`${classes.marginBox} ${
                            formData?.isCopaymentExempted === true ? classes.marginBoxChecked : ""
                          } ${readOnlyView ? classes.marginBoxReadOnly : ""}`}
                          onClick={
                            readOnlyView
                              ? undefined
                              : () =>
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    isCopaymentExempted: true,
                                  }))
                          }
                        >
                          Zuzah- lungs- frei
                        </div>
                        <div
                          role="checkbox"
                          aria-checked={formData?.isCopaymentExempted === false}
                          aria-label="Zuzahlungspflicht"
                          className={`${classes.marginBox} ${
                            formData?.isCopaymentExempted === false ? classes.marginBoxChecked : ""
                          } ${readOnlyView ? classes.marginBoxReadOnly : ""}`}
                          onClick={
                            readOnlyView
                              ? undefined
                              : () =>
                                  setFormData((prev: any) => ({
                                    ...prev,
                                    isCopaymentExempted: false,
                                  }))
                          }
                        >
                          Zuzah- lungs- pflicht
                        </div>
                        <div
                          role="checkbox"
                          aria-checked={!!formData?.isAccident}
                          aria-label="Unfallfolgen"
                          className={`${classes.marginBox} ${
                            formData?.isAccident ? classes.marginBoxChecked : ""
                          } ${readOnlyView ? classes.marginBoxReadOnly : ""}`}
                          onClick={
                            readOnlyView
                              ? undefined
                              : () =>
                                  handleInputChange({
                                    target: { name: "isAccident", value: !formData?.isAccident },
                                  } as any)
                          }
                        >
                          Unfall- folgen
                        </div>
                        <div
                          role="checkbox"
                          aria-checked={!!formData?.isBVG}
                          aria-label="BVG"
                          className={`${classes.marginBox} ${
                            formData?.isBVG ? classes.marginBoxChecked : ""
                          } ${readOnlyView ? classes.marginBoxReadOnly : ""}`}
                          onClick={
                            readOnlyView
                              ? undefined
                              : () =>
                                  handleInputChange({
                                    target: { name: "isBVG", value: !formData?.isBVG },
                                  } as any)
                          }
                        >
                          BVG
                        </div>
                      </div>

                      {/* Patient/insurer box — an in-flow grid cell now (was
                          absolutely positioned at a fixed 3.6in, overflowing
                          its MUI grid column). */}
                        <div
                          className={`${classes.box} ${
                            formData.rxtype === "Entlassmanagement"
                              ? classes.diagonalBackground
                              : ""
                          }`}
                        >
                          {/* <Grid container spacing={2}> */}

                          <Grid item xs={12}>
                            <Typography className={classes.typography}>
                              Krankenkasse bzw. Kostenträger
                            </Typography>
                            <Controls.Input
                              disabled
                              name="Receiptpayer"
                              label=""
                              value={selectedPayer || formData.Receiptpayer}
                              onChange={handleInputChange}
                              style={{
                                width: "100%",
                              }}
                            />

                            <Divider
                              sx={{
                                flexGrow: 1,
                                background: attentionColors.immediateAttention,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography className={classes.typography}>
                              Name, Vorname des Versicherten
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={4}>
                                <Controls.Input
                                  disabled
                                  name=""
                                  label=""
                                  value={patientLastName}
                                  onChange={handleInputChange}
                                  style={{ width: "100%", margin: "0px" }}
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <Controls.Input
                                  disabled
                                  name=""
                                  label=""
                                  value={patientFirstName}
                                  onChange={handleInputChange}
                                  style={{ width: "100%", margin: "0px" }}
                                />
                              </Grid>
                              <Grid item xs={5}>
                                <Typography className={classes.typography}>geb . am</Typography>
                                <Controls.DatePicker
                                  name=""
                                  label=""
                                  value={patientDob}
                                  onChange={handleInputChange}
                                  disabled
                                  variant="standard"
                                  style={{
                                    width: "100%",
                                    padding: "0px",
                                    margin: "0px",
                                    fontSize: "10px",
                                    "& .MuiInputBase-input": {
                                      padding: 0,
                                      margin: 0,
                                      paddingRight: "0px",
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                            <Divider
                              sx={{
                                flexGrow: 1,
                                background: attentionColors.immediateAttention,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={4}>
                                <Typography className={classes.typography}>
                                  Kostenträgerkennung
                                </Typography>

                                <div style={{ height: "30px", position: "relative" }}>
                                  {renderPrefillField(
                                    "chipcard",
                                    <Controls.Input
                                      disabled={readOnlyView}
                                      name="chipcard"
                                      label=""
                                      value={formData?.chipcard ?? ""}
                                      onChange={handleInputChange}
                                      onKeyDown={handleChipCardDataEntry}
                                      onBlur={handleChipCardDataEntry}
                                      // onMouseLeave={handleChipCardDataEntry}
                                      style={{
                                        width: "100%",
                                        cursor: "text",
                                        paddingLeft: "4px",
                                        paddingRight: "4px",
                                      }}
                                      error={rxerrors.chipcard}
                                    />,
                                  )}
                                </div>
                              </Grid>

                              <Grid item xs={4}>
                                <Typography className={classes.typography}>
                                  Versicherten-Nr.
                                </Typography>
                                {renderPrefillField(
                                  "insureeNumber",
                                  <Controls.Input
                                    disabled={readOnlyView}
                                    name="insureeNumber"
                                    label=""
                                    value={formData?.insureeNumber || ""}
                                    onChange={handleInputChange}
                                    style={{ width: "100%", margin: "0px" }}
                                    error={getKvnrError(formData?.insureeNumber)}
                                  />,
                                )}
                              </Grid>
                              <Grid item xs={4}>
                                <Typography className={classes.typography}>Status</Typography>
                                {renderPrefillField(
                                  "insuranceStatus",
                                  <Controls.Select
                                    disabled={readOnlyView}
                                    name="insuranceStatus"
                                    label=""
                                    value={formData?.insuranceStatus || ""}
                                    onChange={handleInputChange}
                                    options={[
                                      { label: "10000", value: "10000" },
                                      { label: "30000", value: "30000" },
                                      { label: "50000", value: "50000" },
                                    ]}
                                    style={{
                                      width: "100%",
                                      marginTop: "0px",
                                      marginBottom: "0px",
                                      fontSize: "10px",
                                    }}
                                  />,
                                )}
                              </Grid>
                            </Grid>
                            <Divider
                              sx={{
                                flexGrow: 1,
                                background: attentionColors.immediateAttention,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={4}>
                                {/* noWrap: at the grid's cell width this long
                                    label otherwise wraps and misaligns the row. */}
                                <Typography className={classes.typography} noWrap>
                                  Betriebsstätten-Nr
                                </Typography>
                                <Controls.Input
                                  disabled={readOnlyView}
                                  name="practiceNumber"
                                  label=""
                                  value={formData?.practiceNumber || ""}
                                  onChange={handleInputChange}
                                  onBlur={handlePracticeNumberInteraction}
                                  onKeyDown={handlePracticeNumberInteraction}
                                  style={{
                                    width: "100%",
                                    margin: "0px",
                                    cursor: "text",
                                    paddingLeft: "4px",
                                    paddingRight: "4px",
                                  }}
                                />
                              </Grid>

                              <Grid item xs={4}>
                                <Typography className={classes.typography}>Arzt-Nr.</Typography>
                                <Controls.Input
                                  disabled={readOnlyView}
                                  name="doctorNumber"
                                  label=""
                                  value={formData?.doctorNumber || ""}
                                  onChange={handleInputChange}
                                  style={{
                                    width: "100%",
                                    margin: "0px",
                                    cursor: "text",
                                    paddingLeft: "4px",
                                    paddingRight: "4px",
                                  }}
                                  onBlur={handleGetDoctorById}
                                  onKeyDown={handleGetDoctorById}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <Typography className={classes.typography}>Datum</Typography>
                                <ValidationTooltip
                                  validity={validationData?.initialDateVAlidation?.validity}
                                  message={validationData?.initialDateVAlidation?.validity}
                                  placement="right"
                                  sx={{ zIndex: 1300 }}
                                >
                                  <Controls.DatePicker
                                    disabled={readOnlyView}
                                    name="date"
                                    label=""
                                    value={formData?.date}
                                    onChange={handleInputChange}
                                    variant="standard"
                                    style={{
                                      width: "100%",
                                      padding: "0px",
                                      margin: "0px",
                                      fontSize: "10px",
                                      "& .MuiInputBase-input": {
                                        padding: 0,
                                        margin: 0,
                                        paddingRight: "0px",
                                      },
                                    }}
                                    formatDate="dd.MM.yy"
                                  />
                                </ValidationTooltip>
                              </Grid>
                            </Grid>
                          </Grid>
                        </div>
                      {/* Right header: form title + therapy-type checkboxes
                          (third headerGrid column). */}
                      <div className={classes.headerRight}>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            whiteSpace: "normal",
                            color: attentionColors.immediateAttention,
                          }}
                        >
                          Heilmittelverordnung 13
                        </Typography>
                        <Controls.Checkbox
                          disabled
                          name=""
                          label={
                            <Typography
                              sx={{
                                fontSize: "12px",
                                whiteSpace: "normal",
                                color: attentionColors.immediateAttention,
                              }}
                            >
                              Physiotherapie
                            </Typography>
                          }
                          value={accountTherapyType === "Physiotherapie"}
                          onChange={handleInputChange}
                        />
                        <Controls.Checkbox
                          disabled
                          name=""
                          label={
                            <Typography
                              sx={{
                                fontSize: "12px",
                                whiteSpace: "normal",
                                color: attentionColors.immediateAttention,
                              }}
                            >
                              Podologische
                            </Typography>
                          }
                          value={false}
                          onChange={handleInputChange}
                        />
                        <Controls.Checkbox
                          disabled
                          name=""
                          label={
                            <Typography
                              sx={{
                                fontSize: "12px",
                                whiteSpace: "normal",
                                color: attentionColors.immediateAttention,
                              }}
                            >
                              Stimm-,Sprech-
                            </Typography>
                          }
                          value={false}
                          onChange={handleInputChange}
                        />
                        <Controls.Checkbox
                          disabled
                          name=""
                          label={
                            <Typography
                              sx={{
                                fontSize: "12px",
                                whiteSpace: "normal",
                                color: attentionColors.immediateAttention,
                              }}
                            >
                              Ergotherapie
                            </Typography>
                          }
                          value={accountTherapyType === "Ergotherapie"}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <Grid container spacing={1} sx={{ paddingTop: "2px" }}>
                      <Grid item xs={12}>
                        <ValidationTooltip
                          validity={validationData?.icdValidation?.validity}
                          message={validationData?.icdValidation?.validity}
                          placement="left"
                          sx={{ zIndex: 1300 }}
                        >
                          <Typography className={classes.typography} sx={{ fontSize: "12px" }}>
                            Behandlungsrelevante Diagnose(n)
                            <br />
                            ICD-10{" "}
                            <Button
                              onClick={handleAddInput}
                              sx={{ padding: 0, minWidth: "20px", height: "12px" }}
                              disabled={readOnlyView}
                            >
                              <Add
                                sx={{
                                  width: "15px",
                                  color: attentionColors.immediateAttention,
                                }}
                              />
                            </Button>
                          </Typography>
                        </ValidationTooltip>
                      </Grid>
                      <Grid item xs={3}>
                        <Box
                          sx={{
                            height: "75px",
                            width: "90%",
                            border: "1px solid lightgray",
                            overflowY: "scroll",
                            overflowX: "hidden",
                            padding: "3px",
                            backgroundColor: "white",
                          }}
                        >
                          {formData.icdCodeEntries.map((icdEntry: any, index: any) => (
                            <div key={`icd-${index}`}>
                              <Controls.Input
                                disabled={readOnlyView}
                                label=""
                                name={`icdCodeEntries-${index}`}
                                value={icdEntry.userText}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  handleInputChange(e, index, icdEntry?.id);
                                }}
                                onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                  if (e.key === "Tab" || e.key === "Enter") {
                                    e.preventDefault();
                                    // handleICDCodeEntrySubmit(formValues.icdCodeEntries[index], index);
                                    // handleICDCodeEntrySubmit(icdEntry, index);
                                    setCurrentICD(icdEntry.userText);
                                  }
                                }}
                              />
                            </div>
                          ))}
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={9}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Controls.TextArea
                          disabled={readOnlyView}
                          name="diagnosisText"
                          rows={3}
                          placeholder=""
                          value={formData?.diagnosisText}
                          onChange={handleInputChange}
                          style={{ flex: 1, height: "75px" }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1} sx={{ paddingTop: "5px" }}>
                      <Grid item xs={12} sx={{ paddingTop: "10px" }}>
                        <Grid container>
                          <Grid item xs={1}>
                            <Typography className={classes.typography} sx={{ fontSize: "12px" }}>
                              Diagnose gruppe
                            </Typography>
                          </Grid>
                          <Grid item xs={1}>
                            <div
                              style={{
                                position: "absolute",
                                width: "0.6in",
                                height: "0.3in",
                                backgroundColor: "white",
                              }}
                            >
                              <Controls.Select
                                disabled={readOnlyView}
                                name="diagnosegroup"
                                label=""
                                // id={1}
                                value={formData?.diagnosegroup}
                                onChange={handleInputChange}
                                options={filteredDiagnoseGroups?.map((option: any) => ({
                                  label: option?.code.toString(),
                                  value: option?.code.toString(),
                                  id: option?.id.toString(),
                                }))}
                                style={{
                                  width: "100%",
                                  marginTop: "0px",
                                  marginBottom: "0px",
                                  fontSize: "10px",
                                }}
                              />
                            </div>
                          </Grid>
                          <Grid item xs={2} sx={{ paddingLeft: "12px", paddingTop: "1px" }}>
                            <Typography className={classes.typography} sx={{ fontSize: "12px" }}>
                              Leitsymptomatik
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "10px",
                                color: attentionColors.immediateAttention,
                              }}
                            >
                              gemäß Heilmittelkatalog
                            </Typography>
                          </Grid>
                          <Grid item xs={5} sx={{ paddingLeft: "15px" }}>
                            <Controls.Checkbox
                              name="a"
                              label="a"
                              value={formData?.a}
                              onChange={handleInputChange}
                              disabled={!selectedDiagnoseGroup?.a || readOnlyView}
                            />
                            <Controls.Checkbox
                              name="b"
                              label="b"
                              value={formData?.b}
                              onChange={handleInputChange}
                              disabled={!selectedDiagnoseGroup?.b || readOnlyView}
                            />
                            <Controls.Checkbox
                              name="c"
                              label="c"
                              value={formData?.c}
                              onChange={handleInputChange}
                              disabled={!selectedDiagnoseGroup?.c || readOnlyView}
                            />
                            <Controls.Checkbox
                              name="d"
                              label="d"
                              value={formData?.d}
                              onChange={handleInputChange}
                              disabled={!selectedDiagnoseGroup?.d || readOnlyView}
                            />
                          </Grid>
                          <Grid item xs={3} sx={{ paddingLeft: "3px" }}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              name="hasIndividualSymptoms"
                              label={
                                <Typography sx={{ fontSize: "10px" }}>
                                  Patientenindividuelle Leitsymptomatik
                                </Typography>
                              }
                              value={formData?.hasIndividualSymptoms}
                              onChange={handleInputChange}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={9}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "10px",
                          color: attentionColors.immediateAttention,
                        }}
                      >
                        Leitsymptomatik (patientenindividuelle Leitsymptomatik als Freitext angeben)
                      </Typography>
                      <Controls.TextArea
                        name="symptoms"
                        disabled={readOnlyView}
                        rows={3}
                        placeholder=""
                        value={formData?.symptoms}
                        onChange={handleInputChange}
                        style={{ flex: 1, height: "50px " }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ValidationTooltip
                        validity={validationData?.therapyValidations?.validity}
                        message={validationData?.therapyValidations?.validity}
                        placement="left"
                        sx={{ zIndex: 1300 }}
                      >
                        <Typography className={classes.typography}>
                          Heilmittel nach Maßgabe des Kataloges
                        </Typography>
                      </ValidationTooltip>
                      <Grid item xs={12} sx={{ maxHeight: "140px", overflowY: "auto" }}>
                        <Table
                          sx={{
                            borderCollapse: "collapse",
                            width: "100%",
                            border: "1px solid red",
                            background: "white",
                          }}
                        >
                          <TableBody sx={{ maxHeight: "50px", overflowY: "scroll" }}>
                            {formData?.therapyRxs
                              ?.filter((therapy) => !therapy.therapy.isSupplementary) // Exclude supplementary therapies
                              .map((therapy, rowIndex) => (
                                <TableRow
                                  key={therapy.id}
                                  // style={{ border: "1px solid red", height: "1px" }}
                                >
                                  <TableCell
                                    sx={{
                                      padding: "0px",
                                      width: "50px",
                                      height: "1px",
                                    }}
                                  >
                                    <TherapyTermField
                            therapyType={accountTherapyType}
                                      disabled={readOnlyView}
                                      name="therapy"
                                      label=""
                                      value={therapy.therapy.abbreviation}
                                      onChange={(event: any) =>
                                        handleInputChange(event, rowIndex, therapy?.id)
                                      }
                                      // options={therapyOptions[0]
                                      //   ?.filter((therapyObj: any) => therapyObj.duration)
                                      //   ?.map((therapy: any) => ({
                                      //     label: therapy?.abbreviation?.toString(),
                                      //     value: therapy?.abbreviation?.toString(),
                                      //   }))}
                                      style={{
                                        width: "110px",
                                        marginTop: "0px",
                                        marginBottom: "0px",
                                        fontSize: "10px",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      padding: "0px",
                                      direction: "column",
                                      height: "1px",
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "10px" }}>
                                      {therapy.therapy.description}
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      padding: "0px",
                                      width: "30px",
                                      height: "1px",
                                    }}
                                  >
                                    <Controls.Checkbox
                                      name="doubled"
                                      value={therapy.isDoubled}
                                      label={
                                        <Typography sx={{ fontSize: "10px" }}>
                                          Doppelbehandlung
                                        </Typography>
                                      }
                                      disabled={!therapy.therapy.abbreviation || readOnlyView}
                                      onChange={(event: any) => {
                                        handleDuplicateRow(rowIndex, event, therapy.id);
                                      }}
                                    />
                                  </TableCell>
                                  {rowIndex === 0 && (
                                    <ValidationTooltip
                                      validity={validationData?.rxAmountValidation?.validity}
                                      message={validationData?.rxAmountValidation?.validity}
                                      placement="right"
                                      sx={{ zIndex: 1300 }}
                                    >
                                      <TableCell
                                        sx={{
                                          padding: "0px",
                                          width: "30px",
                                          height: "1px",
                                          borderLeft: "1px solid red",
                                        }}
                                      >
                                        <Controls.Input
                                          name="amount"
                                          disabled={readOnlyView}
                                          label=""
                                          value={therapy.amount}
                                          onChange={(
                                            event: React.ChangeEvent<HTMLInputElement>
                                          ) => {
                                            handleInputChange(event, rowIndex, therapy?.id);
                                          }} // Pass rowIndex to handleInputChange
                                          style={{ width: "100%", margin: "0px" }}
                                        />
                                      </TableCell>
                                    </ValidationTooltip>
                                  )}
                                  {rowIndex !== 0 && (
                                    <TableCell
                                      sx={{
                                        padding: "0px",
                                        width: "30px",
                                        height: "1px",
                                        borderLeft: "1px solid red",
                                      }}
                                    >
                                      <Controls.Input
                                        name="amount"
                                        disabled={readOnlyView}
                                        label=""
                                        value={therapy.amount}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                          handleInputChange(event, rowIndex, therapy?.id);
                                        }} // Pass rowIndex to handleInputChange
                                        style={{ width: "100%", margin: "0px" }}
                                      />
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </Grid>

                      <Typography
                        sx={{
                          fontSize: "10px",
                          color: attentionColors.immediateAttention,
                        }}
                      >
                        Ergänzendes Heilmittel
                      </Typography>
                      <Grid item xs={12}>
                        <Table
                          sx={{
                            borderCollapse: "collapse",
                            width: "100%",
                            border: "1px solid red",
                            background: "white",
                          }}
                        >
                          <TableBody sx={{ maxheight: "10px", overflowY: "scroll" }}>
                            {/* Render Supplementary Therapies */}
                            {formData?.therapyRxs
                              ?.filter((therapy) => therapy.therapy.isSupplementary)
                              .map((therapy: any, supplementaryIndex) => (
                                <TableRow key={therapy.id}>
                                  <TableCell sx={{ padding: "0px", width: "50px", height: "1px" }}>
                                    <TherapyTermField
                            therapyType={accountTherapyType}
                            isSupplementary
                                      disabled={readOnlyView}
                                      name="therapy"
                                      value={therapy?.therapy?.abbreviation || ""} // Default empty if no therapy
                                      onChange={(event: any) =>
                                        handleInputChange(event, supplementaryIndex, therapy?.id)
                                      }
                                      style={{
                                        width: "110px",
                                        marginTop: "0px",
                                        marginBottom: "0px",
                                        fontSize: "10px",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ padding: "0px", height: "1px" }}>
                                    <Typography sx={{ fontSize: "10px" }}>
                                      {therapy?.therapy?.description || "Keine Ergänzungstherapie"}
                                    </Typography>
                                  </TableCell>
                                  <TableCell sx={{ padding: "0px", width: "30px", height: "1px" }}>
                                    <Controls.Checkbox
                                      name="doubled"
                                      value={therapy?.isDoubled || false}
                                      label={
                                        <Typography sx={{ fontSize: "10px" }}>
                                          Doppelbehandlung
                                        </Typography>
                                      }
                                      disabled={!therapy?.therapy?.abbreviation || readOnlyView}
                                      onChange={(event: any) =>
                                        handleDuplicateRow(supplementaryIndex, event, therapy?.id)
                                      }
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      padding: "0px",
                                      width: "30px",
                                      height: "1px",
                                      borderLeft: "1px solid red",
                                    }}
                                  >
                                    <Controls.Input
                                      name="amount"
                                      value={therapy?.amount || ""}
                                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        handleInputChange(event, supplementaryIndex, therapy?.id)
                                      }
                                      disabled={readOnlyView}
                                      style={{ width: "100%", margin: "0px" }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container>
                          <Grid item xs={3}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              name="therapyReport"
                              label={
                                <Typography className={classes.typography}>
                                  {" "}
                                  Therapiebericht
                                </Typography>
                              }
                              value={formData?.therapyReport}
                              onChange={handleInputChange}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <Box display="flex" alignItems="center">
                              <Typography
                                variant="body1"
                                className={classes.typography}
                                sx={{ marginRight: "8px" }}
                              >
                                Hausbesuch
                              </Typography>
                              <FormControlLabel
                                label=""
                                control={
                                  <>
                                    <Checkbox
                                      checked={formData.isHomeVisit === true}
                                      onChange={handleCheckboxChange}
                                      disabled={readOnlyView}
                                      name="visitTypeCheckboxJa"
                                      value="yes"
                                    />
                                    <Typography variant="body1">ja</Typography>
                                    <Checkbox
                                      checked={formData.isHomeVisit === false}
                                      onChange={handleCheckboxChange}
                                      disabled={readOnlyView}
                                      name="visitTypeCheckboxNein"
                                      value="no"
                                    />
                                    <Typography variant="body1">nein</Typography>
                                  </>
                                }
                              />
                            </Box>
                          </Grid>

                          <Grid item xs={4}>
                            <ValidationTooltip
                              validity={validationData?.therapyFrequencyValidation?.validity}
                              message={validationData?.therapyFrequencyValidation?.validity}
                              placement="right"
                              sx={{ zIndex: 1300 }}
                            >
                              <Grid container>
                                <Grid item xs={4} sx={{ paddingTop: "2px" }}>
                                  <Typography className={classes.typography}>
                                    Therapie-frequenz
                                  </Typography>
                                </Grid>
                                <Grid
                                  item
                                  xs={6}
                                  sx={{
                                    background: "white",
                                    paddingTop: "2px",
                                    borderBottom: `1px solid ${attentionColors.immediateAttention}`,
                                  }}
                                >
                                  <Controls.Input
                                    disabled={readOnlyView}
                                    name="therapyFrequency"
                                    label=""
                                    value={
                                      Array.from(
                                        new Set(
                                          formData.therapyRxs.map(
                                            (therapy: any) => therapy.frequency?.id
                                          )
                                        )
                                      )[0]
                                    }
                                    onChange={handleInputChange}
                                    style={{ width: "100%", margin: "0px" }}
                                  />
                                </Grid>
                              </Grid>
                            </ValidationTooltip>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container>
                          <Grid item xs={6}>
                            <Controls.Checkbox
                              disabled={readOnlyView}
                              name="urgent"
                              label={
                                <Typography className={classes.typography}>
                                  Dringlicher Behandlungsbedarf innerhalb von 14 Tagen
                                </Typography>
                              }
                              value={formData?.urgent}
                              onChange={handleInputChange}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      {/* <Grid item xs={12}> */}
                      <Grid container>
                        <Grid
                          item
                          xs={7}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            paddingTop: "5px",
                          }}
                        >
                          <Controls.TextArea
                            label={
                              <Typography className={classes.typography}>
                                ggf. Thrapieziele/weitre med. Befunde und Himweise
                              </Typography>
                            }
                            disabled={readOnlyView}
                            name="additionalNotes"
                            rows={4}
                            placeholder=""
                            value={formData.additionalNotes || ""}
                            onChange={handleInputChange}
                            style={{ flex: 1, height: "80px " }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={5}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            paddingLeft: "20px",
                          }}
                        >
                          {/* <TextArea
                rows={7}
                cols={100}
                value={`${formData.doctorData?.contactInfo?.salutation} ${formData.doctorData?.contactInfo?.firstNames} ${formData.doctorData?.contactInfo?.lastName}\n` +
                  `${formData.doctorData?.contactInfo?.addressLine1}\n` +
                  `${formData.doctorData?.contactInfo?.addressLine2}\n` +
                  `Tel: ${formData.doctorData?.contactInfo?.tel}\n` + // Added "Tel:" label
                  `BSNR: ${formData.doctorData?.clinicNumber}` // Added "BSNR:" label
                }
                onChange={handleInputChange}
                sx={{ width: '50px', textAlign: 'justify', maxHeight: "80px", overflowY: "scroll" }} /> */}

                          {/* <strong>ID:</strong> {formData.doctorNumber || ""}
                  </div>
                  <div>
                    <strong>Vorname:</strong> {displayDoctor.contactInfo.firstNames}
                  </div>
                  <div>
                    <strong>Nachname:</strong> {displayDoctor.contactInfo.lastName}
                  </div>
                  <div>
                    <strong>Email:</strong> {displayDoctor.contactInfo.email} */}
                          <Controls.TextArea
                            name="doctorData"
                            rows={5}
                            placeholder=""
                            value={
                              `${currentDoctor?.contactInfo?.salutation || ""} ${
                                currentDoctor?.contactInfo?.firstNames || ""
                              } ${currentDoctor?.contactInfo?.lastName || ""}\n` +
                              `${currentClinic?.contactInfo?.addressLine1 || ""}\n` +
                              `${currentClinic?.contactInfo?.addressLine2 || ""}\n` +
                              `Tel: ${currentClinic?.contactInfo?.phone1 || ""}\n` +
                              `BSNR: ${currentClinic?.BSNR || ""}`
                            }
                            onChange={handleInputChange}
                            disabled
                            style={{ flex: 1, height: "80px " }}
                          />
                        </Grid>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography className={classes.typography}>
                              IK des Leitungserbringers
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Box
                              sx={{
                                background: "white",
                                width: "100px",
                                height: "20px",
                              }}
                            ></Box>
                          </Grid>
                        </Grid>
                      </Grid>
                      {/* </Grid> */}
                    </Grid>
                  </Grid>
                </Grid>
              </form>
              <Modal
                open={loading}
                aria-labelledby="loading-modal-title"
                aria-describedby="loading-modal-description"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div>
                  <CircularProgress size={60} />
                </div>
              </Modal>
            </Container>
          </ScalableComponentSimple>
        </Grid>
        {!readOnlyView && (
          <Grid item xs={12} md={3} sx={{ border: "0px dotted black" }}>
            <FlatCard title="Validierung">
              <ValidationCard
                patientData={patientValues}
                selectedRx={
                  formData
                  // patientValues?.rxs?.filter(
                  //   (rx: any) => rx.rxNumber === rxFormData?.rxNumber
                  // )[0]
                }
                form={formType.LONG}
              />
            </FlatCard>
          </Grid>
        )}
        {unsavedChangesWarning && (
          <WarningDialog
            open={true} // Ensure the warning dialog is open
            onClose={handleCloseUnsavedChangesWarning} // Function to close the dialog
            onContinue={handleUnsavedChangesWarning} // Function to continue despite unsaved changes
            title="Änderungen speichern?"
            content="Du hast einige Änderungen gemacht. Möchtest du diese speichern, bevor du schließt?"
            isConfirmSave
          />
        )}
      </Grid>
    );

    // if (!selectedRxId  ||  !entryType) {
    //   return (
    //     <Box p={2}>
    //       <span>Kein Rezept ausgewählt</span>
    //     </Box>
    //   );
    // }

    if (isRxLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress />
        </Box>
      );
    }

    if (rxError) {
      return (
        <Box p={2}>
          <span>Fehler beim Laden des Rezepts für {selectedRxId}</span>
        </Box>
      );
    }
    return !readOnlyView ? (
      <Dialog
        open={openRx as boolean}
        // While a save is in flight the dialog must not be dismissed — by the
        // backdrop, by Escape, or by Abbrechen. Closing it mid-request unmounts
        // the component, and react-query drops the per-call onSuccess of an
        // unmounted caller, so the save would land server-side with nothing on
        // screen reflecting it.
        onClose={isSavingRx ? undefined : handleCloseRxDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle> {`${formData?.rxtype} Dialog`}</DialogTitle>
        <DialogContent>{FormContent}</DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseRxDialog} disabled={isSavingRx}>
            Abbrechen
          </Button>
          <Button
            variant="outlined"
            onClick={handleOpenDocumentViewer}
            disabled={isSavingRx}
            sx={{
              backgroundColor: "#f5f5f5",
              color: "#1976d2",
              "&:hover": { backgroundColor: "#e0e0e0" },
            }}
          >
            📄 Document Viewer
          </Button>
          {/*
            Speichern is disabled by any entry in `rxerrors`, but the form only
            ever rendered some of them, so the button could sit dead with no
            visible cause. The tooltip names what is blocking it; `span` because
            MUI swallows pointer events on a disabled button, and a Tooltip on
            one never fires without a wrapper.
          */}
          <Tooltip title={saveBlockedReason}>
            <span>
              <Button
                onClick={handleGenericSubmit}
                variant="contained"
                disabled={isSavingRx || saveBlockedReason !== ""}
                startIcon={
                  isSavingRx ? <CircularProgress size={16} color="inherit" /> : undefined
                }
              >
                {isSavingRx ? "Speichern…" : "Speichern"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
        <VerordnungDialog
          openAddEditDialog={isClinicModalOpen}
          handleCloseAddEditDialog={() => { setIsClinicModalOpen(false); setIsClinicModalEditMode(false); }}
          clinicId={currentClinicId}
          setCreationResponse={setCreationResponse}
          doctorId={currentDoctorId}
          currentClinic={currentClinic}
          currentDoctor={currentDoctor}
          initialDoctorName={autocompleteSearchTerm}
          isEditMode={isClinicModalEditMode}
        />
        {isDocumentViewerOpen && (
          <SimpleDocumentViewer
            open={isDocumentViewerOpen}
            onClose={handleCloseDocumentViewer}
            patientId={patientValues?.id || undefined}
          />
        )}
      </Dialog>
    ) : (
      FormContent
    );
  }
);

export default Verordnung13;
